/**
 * SmartResponse AI Meta Lead Gen, Exclusion Pool Hub, Segmented Campaign Builder,
 * Meta Graph API v25.0 Execution, Guardian Alerts, AI Context & Competition Portal API client.
 *
 * Production Verified Infrastructure:
 *   - Meta Business Portfolio: Afima (209169658044315)
 *   - Meta Ad Account: Afima - PremiePortalen (SmartResponse) (act_995004339585761)
 *   - Meta Facebook Page (NO): PremiePortalen (1420182721178198)
 *   - Live Norwegian Portal: https://premieportal.com
 *   - SmartResponse FeedOrder API: https://webservice.smartresponse-media.com/FeedOrder.aspx
 *     (sourceId=100168, partnerId=1336)
 */
import { API_BASE_URL } from '../config';

export type SRGeo = 'all' | 'dk' | 'no';

export interface SRSponsor {
  name: string;
  cvr: string;
  country: string;
  category: string;
  channels: string[];
  description: string;
  privacy_url: string;
  optout_email: string;
}

export interface SRQualifyingQuestion {
  key: string;
  label: string;
  options: string[];
  disqualify_values: string[];
}

export interface SRCompetition {
  comp_key: string;
  cloak_slug?: string;
  provider_network?: string;
  start_date_iso?: string;
  end_date_iso?: string;
  paid_lead_age_range?: string;
  days_until_end?: number;
  days_since_start?: number;
  in_new_tab?: boolean;
  in_ending_soon_tab?: boolean;
  sr_campaign_id: string;
  sr_affiliate_id: string;
  geo: 'dk' | 'no';
  category: string;
  title: string;
  subtitle: string;
  prize_name: string;
  prize_value_dkk: number;
  payout_dkk: number;
  image_url?: string;
  tracking_url_template: string;
  exclusion_pools: string[];
  target_age_min: number;
  target_age_max: number;
  target_genders: number[];
  target_interests: { id: string; name: string }[];
  excluded_zips: string[];
  qualifying_questions: SRQualifyingQuestion[];
  sponsors: SRSponsor[];
  terms_draw_date: string;
  show_on_offerwall: boolean;
  offerwall_priority: number;
  status: 'active' | 'paused' | 'archived' | 'expired';
}

export interface SRPortalBrand {
  geo: 'dk' | 'no';
  brand_name: string;
  domain: string;
  portal_path: string;
  privacy_path: string;
  company_name: string;
  company_cvr: string;
  company_address: string;
  contact_email: string;
  primary_color: string;
  accent_color: string;
  meta_ad_account_id: string;
  meta_page_id: string;
  meta_pixel_id: string;
  sr_affiliate_id: string;
}

export interface SRExclusionPool {
  pool_key: string;
  name: string;
  geo: 'dk' | 'no';
  description: string;
  retention_days: number;
  meta_custom_audience_id: string;
  meta_engagement_audience_id?: string | null;
  auto_add_submitted_leads: boolean;
  auto_add_sr_rejections: boolean;
  total_hashes: number;
  em_sha256_count: number;
  ph_sha256_count: number;
  md5_count: number;
  rainbow_resolved_count: number;
  sr_import_count: number;
  internal_collected_count: number;
  linked_competitions: string[];
  last_synced_at: string;
}

export interface SRSuppressionImportAudit {
  id: number;
  pool_key: string;
  filename: string;
  input_format: string;
  total_rows: number;
  valid_hashes: number;
  inserted_new: number;
  rainbow_resolved: number;
  meta_sync_status: string;
  created_at: string;
}

export interface SRDashboardSummary {
  spend_dkk: number;
  raw_leads: number;
  pre_dedup_blocked: number;
  sr_api_duplicates: number;
  sr_api_accepted: number;
  duplicate_rate_pct: number;
  gross_cpl_dkk: number;
  net_accepted_cpl_dkk: number;
  primary_revenue_dkk: number;
  offerwall_clicks: number;
  offerwall_conversions: number;
  offerwall_revenue_dkk: number;
  total_revenue_dkk: number;
  net_profit_dkk: number;
  roas: number;
}

export interface SRCompetitionPerfRow {
  comp_key: string;
  sr_campaign_id: string;
  geo: string;
  category: string;
  title: string;
  prize_name: string;
  payout_dkk: number;
  exclusion_pools: string[];
  spend_dkk: number;
  raw_leads: number;
  pre_dedup_blocked: number;
  sr_api_duplicates: number;
  sr_api_accepted: number;
  duplicate_rate_pct: number;
  gross_cpl_dkk: number;
  net_accepted_cpl_dkk: number;
  primary_revenue_dkk: number;
  offerwall_clicks: number;
  offerwall_conversions: number;
  offerwall_revenue_dkk: number;
  total_revenue_dkk: number;
  net_profit_dkk: number;
  roas: number;
  status: string;
}

export interface SRAgeSegmentRow {
  age_bucket: string;
  spend_dkk: number;
  raw_leads: number;
  accepted_leads: number;
  duplicate_rate_pct: number;
  gross_cpl_dkk: number;
  net_accepted_cpl_dkk: number;
  offerwall_epc_dkk: number;
  roas: number;
  recommendation: string;
}

export interface SRAdWinnerRow {
  campaign_id?: string;
  campaign_name?: string;
  adset_id?: string;
  adset_name?: string;
  ad_id?: string;
  ad_name: string;
  spend_dkk: number;
  impressions: number;
  clicks: number;
  ctr_pct: number;
  meta_leads: number;
  sr_accepted_leads: number;
  net_cpl_dkk: number;
  revenue_dkk: number;
  roas: number;
}

export interface SRGuardianAlert {
  rule: string;
  severity: 'critical' | 'warning' | 'info';
  comp_key: string;
  title: string;
  message: string;
  recommended_action: string;
}

export interface SRGuardianHealthCheck {
  key: string;
  label: string;
  status: 'ok' | 'warning' | 'error';
  detail: string;
}

export interface SRGuardianOverview {
  config: {
    mode: 'alert_only' | 'auto_pause';
    max_spend_zero_accepted_dkk: number;
    max_duplicate_rate_pct: number;
    max_cpl_multiplier: number;
    min_leads_for_dup_alert: number;
    updated_at: string;
  };
  alerts_count: number;
  alerts: SRGuardianAlert[];
  health_checks: SRGuardianHealthCheck[];
  total_leads_tracked: number;
}

export interface SRCampaignPlanRecord {
  plan_id: string;
  plan_key: string;
  comp_key: string;
  sr_campaign_id: string;
  geo: 'dk' | 'no';
  name: string;
  daily_budget_dkk: number;
  status: 'draft' | 'validated' | 'queued' | 'dry_run_built' | 'built_paused' | 'active' | 'paused' | 'failed';
  mode: string;
  ad_account_id: string;
  page_id: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  built_at?: string | null;
  meta_objects?: {
    ad_account_id?: string;
    page_id?: string;
    image_hash?: string;
    lead_form_id?: string;
    campaign_id?: string;
    adsets?: { segment_key: string; name: string; id: string }[];
    ads?: {
      segment_key: string;
      name: string;
      id: string;
      ad_id?: string;
      creative_id: string;
      image_hash?: string;
      preview_url?: string;
      target_age_label?: string;
      local_image_url?: string;
    }[];
  };
  build_calls?: {
    step: number;
    object_type: string;
    method: string;
    path: string;
    body?: any;
    summary: string;
  }[];
  last_error?: string | null;
  options?: Record<string, any>;
  plan: any;
}

export interface SRLeadLedgerRow {
  id: number;
  leadgen_id: string;
  comp_key: string;
  sr_campaign_id: string;
  geo: string;
  campaign_name: string;
  adset_name: string;
  ad_name: string;
  em_hash: string;
  ph_hash: string;
  age_bucket: string;
  gender: string;
  zip_code: string;
  current_operator?: string;
  disqualify_reason?: string;
  consent_given: boolean;
  dedup_status: string;
  matched_pool_key?: string | null;
  sr_api_status: string;
  payout_dkk: number;
  capi_dispatched: boolean;
  capi_event_id?: string | null;
  offerwall_clicks: number;
  offerwall_conversions: number;
  offerwall_revenue_dkk: number;
  orderfeed_params?: Record<string, any> | null;
  orderfeed_response?: string;
  created_at: string;
}

export interface SRDashboardOverview {
  geo: string;
  days: number;
  safety_modes: {
    sr_api_mode: string;
    meta_capi_mode: string;
    meta_builder_mode: string;
  };
  meta_insights_status?: {
    account_id: string;
    account_name: string;
    last_synced_at: string | null;
    sync_mode: string;
  };
  summary: SRDashboardSummary;
  by_competition: SRCompetitionPerfRow[];
  by_age_segment: SRAgeSegmentRow[];
  by_gender: {
    gender: string;
    raw_leads?: number;
    accepted_leads?: number;
    spend_dkk?: number;
    share_pct: number;
    gross_cpl_dkk: number;
    net_accepted_cpl_dkk: number;
    duplicate_rate_pct: number;
    offerwall_ctr_pct: number;
    roas: number;
  }[];
  ad_winners?: SRAdWinnerRow[];
  guardian?: SRGuardianOverview;
  campaign_plans?: SRCampaignPlanRecord[];
  recent_leads: SRLeadLedgerRow[];
  brands: SRPortalBrand[];
}

const SPONSORS_EFORBRUGER: SRSponsor[] = [
  {
    name: 'E-forbruger / SmartResponse A/S (Leadshare)',
    cvr: '25056478',
    country: 'DK',
    category: 'Leadshare Partnernetværk',
    channels: ['email', 'sms', 'phone'],
    description: 'Markedsføring på telefon, SMS og e-mail fra tilknyttede E-forbruger virksomheder.',
    privacy_url: 'https://ms.e-forbruger.dk/',
    optout_email: 'info@smartresponse.dk',
  },
];

const SPONSORS_TRYG: SRSponsor[] = [
  {
    name: 'Tryg Forsikring A/S (Standalone)',
    cvr: '24260666',
    country: 'DK',
    category: 'Forsikring',
    channels: ['phone', 'email', 'sms'],
    description: 'Uforpligtende forsikringstilbud fra Tryg. Konkurrencen er ikke sponsoreret af Silvan.',
    privacy_url: 'https://tryg.dk/om-tryg/privatlivspolitik',
    optout_email: 'kundeservice@tryg.dk',
  },
];

const SPONSORS_ASE: SRSponsor[] = [
  {
    name: 'Ase (Standalone)',
    cvr: '55860116',
    country: 'DK',
    category: 'A-kasse, Fagforening & Lønsikring',
    channels: ['phone', 'email', 'sms'],
    description: 'Kontakt og rådgivning om a-kasse og fagforening fra Ase. Konkurrencen er ikke sponsoreret af Apollo.',
    privacy_url: 'https://www.ase.dk/privatlivspolitik',
    optout_email: 'ase@ase.dk',
  },
];

const SPONSORS_TALKMORE: SRSponsor[] = [
  {
    name: 'Talkmore AS (Standalone — Telenor-dekning)',
    cvr: 'NO-986729472',
    country: 'NO',
    category: 'Mobilabonnement',
    channels: ['phone', 'sms', 'email'],
    description: 'Uforpliktende tilbud på mobilabonnement med full Telenor-dekning fra Talkmore AS.',
    privacy_url: 'https://talkmore.no/personvern',
    optout_email: 'kundesenter@talkmore.no',
  },
];

const SPONSORS_ONECALL: SRSponsor[] = [
  {
    name: 'OneCall / Telia Norge AS (Standalone)',
    cvr: 'NO-981929057',
    country: 'NO',
    category: 'Mobilabonnement',
    channels: ['phone', 'sms', 'email'],
    description: 'Uforpliktende tilbud på mobilabonnement og familiepakker fra OneCall.',
    privacy_url: 'https://onecall.no/personvern',
    optout_email: 'kundeservice@onecall.no',
  },
];


const SPONSORS_SPAREKLUBBEN: SRSponsor[] = [
  {
    name: 'Spareklubb Norge / NorskeRabatter (NordicLeads)',
    cvr: 'NO-NORDICLEADS',
    country: 'NO',
    category: 'Gavekort- & Fordelsklubb (Co-Reg / Leadshare)',
    channels: ['phone', 'email', 'sms'],
    description: 'Konkurransen er et tilbud fra SpareklubbNorge / NorskeRabatter og markedsføres i samarbeid med NordicLeads. Premien kjøpes hos GoGift eller Glede (verdi 1.000–15.000 kr.). Personer 18 år eller eldre i Norge kan delta.',
    privacy_url: 'https://konkurranse.spareklubbnorge.com/',
    optout_email: 'ingrid@spareklubbnorge.com',
  },
];

const SPONSORS_NORTHGUARD: SRSponsor[] = [
  {
    name: 'Northguard Norge AS (Standalone Annoncør)',
    cvr: 'NO-NORTHGUARD',
    country: 'NO',
    category: 'Sikkerhed & Alarm',
    channels: ['phone', 'email'],
    description: 'Ved din deltagelse gir du samtykke til å bli kontaktet av Northguard Norge AS via telefon og e-post angående tilbud.',
    privacy_url: 'https://ms.smartresponse-media.com/Files/114805/Conditions.html',
    optout_email: 'info@smartresponse.dk',
  },
];

const SPONSORS_ALLENTE: SRSponsor[] = [
  {
    name: 'Allente Norge AS (Standalone Annoncør)',
    cvr: 'NO-979428622',
    country: 'NO',
    category: 'TV & Streaming',
    channels: ['phone', 'email', 'sms'],
    description: 'Uforpliktende tilbud på TV- og strømmepakker fra Allente Norge AS.',
    privacy_url: 'https://www.allente.no/personvern/',
    optout_email: 'kundeservice@allente.no',
  },
];

const FALLBACK_BRANDS: SRPortalBrand[] = [
  {
    geo: 'no',
    brand_name: 'PremiePortalen',
    domain: 'premieportal.com',
    portal_path: '/konkurrencer/',
    privacy_path: '/cookie-og-persondatapolitik/',
    company_name: 'Afima ApS',
    company_cvr: 'DK-43918210',
    company_address: 'Oslo / København',
    contact_email: 'kontakt@premieportal.com',
    primary_color: '#0F3F34',
    accent_color: '#F07824',
    meta_ad_account_id: 'act_995004339585761',
    meta_page_id: '1420182721178198',
    meta_pixel_id: 'pixel_sr_no_placeholder',
    sr_affiliate_id: '1336',
  },
  {
    geo: 'dk',
    brand_name: 'PræmiePortalen.dk',
    domain: 'praemieportalen.dk',
    portal_path: '/konkurrencer/',
    privacy_path: '/cookie-og-persondatapolitik/',
    company_name: 'Afima ApS',
    company_cvr: 'DK-43918210',
    company_address: 'København, Danmark',
    contact_email: 'kontakt@praemieportalen.dk',
    primary_color: '#0F3F34',
    accent_color: '#F07824',
    meta_ad_account_id: 'act_995004339585761',
    meta_page_id: 'page_sr_dk_placeholder',
    meta_pixel_id: 'pixel_sr_dk_placeholder',
    sr_affiliate_id: '1336',
  },
];

const FALLBACK_COMPETITIONS: SRCompetition[] = [
  {
    comp_key: 'no_onecall_rema1000_114927',
    cloak_slug: 'rema-1000',
    provider_network: 'SmartResponse',
    start_date_iso: '2026-10-02',
    end_date_iso: '2026-12-22',
    paid_lead_age_range: '25–70',
    sr_campaign_id: '114927',
    sr_affiliate_id: '1336',
    geo: 'no',
    category: 'grocery',
    title: 'Vinn et gavekort til REMA 1000 på 5.000 kr.',
    subtitle: 'Delta gratis i trekningen om et gavekort på 5.000 kr. til REMA 1000! Svar på 2 korte spørsmål – det tar kun 30 sekunder.',
    prize_name: 'REMA 1000 Gavekort 5.000 kr. (OneCall)',
    prize_value_dkk: 5000,
    payout_dkk: 21.1,
    tracking_url_template: 'https://ms.svarnorge.no/Product.aspx?productId=114927&offerId=#oid#&affiliateId=#affid#&requestid=#reqid#&subid1=#s1#&subid2=#s2#',
    exclusion_pools: ['pool_no_onecall', 'pool_global_blacklist'],
    target_age_min: 25,
    target_age_max: 65,
    target_genders: [],
    target_interests: [{ id: '6003349442621', name: 'Dagligvarer & REMA 1000' }],
    excluded_zips: [],
    qualifying_questions: [
      {
        key: 'current_operator',
        label: 'Hvilken mobiloperatør har du i dag?',
        options: [
          'Telenor',
          'Talkmore',
          'Ice',
          'Chili Mobil',
          'Happybytes',
          'PlussMobil',
          'Fjordkraft',
          'Telia',
          'OneCall',
          'MyCall',
          'Annet',
        ],
        disqualify_values: ['OneCall', 'Telia', 'MyCall'],
      },
      {
        key: 'company_phone',
        label: 'Har du firmabetalt mobilabonnement?',
        options: ['Nei, jeg betaler selv (Privat)', 'Ja, arbeidsgiver betaler (Bedrift)'],
        disqualify_values: ['Ja, arbeidsgiver betaler (Bedrift)'],
      },
    ],
    sponsors: SPONSORS_ONECALL,
    terms_draw_date: '22. desember 2026',
    show_on_offerwall: true,
    offerwall_priority: 10,
    status: 'active',
  },
  {
    comp_key: 'no_talkmore_gogift_114903',
    cloak_slug: 'talkmore-gogift',
    provider_network: 'SmartResponse',
    start_date_iso: '2026-09-17',
    end_date_iso: '2026-11-19',
    paid_lead_age_range: '25–70',
    sr_campaign_id: '114903',
    sr_affiliate_id: '1336',
    geo: 'no',
    category: 'grocery',
    title: 'Talkmore: Vinn universalgavekort fra GoGift (Direkte)',
    subtitle: 'Velg fritt blant hundrevis av butikker med GoGift! Gi samtykke til Talkmore for å delta (40 NOK / 26,40 DKK).',
    prize_name: 'GoGift Supergavekort 5.000 kr (Talkmore)',
    prize_value_dkk: 5000,
    payout_dkk: 26.4,
    tracking_url_template: 'https://ms.svarnorge.no/Product.aspx?productId=114903&groupId=1486&offerId=#oid#&affiliateId=#affid#&requestid=#reqid#&subid1=#s1#&subid2=#s2#&utm_source=smr&utm_medium=affiliate&utm_campaign=talkmore',
    exclusion_pools: ['pool_no_talkmore', 'pool_global_blacklist'],
    target_age_min: 25,
    target_age_max: 65,
    target_genders: [],
    target_interests: [{ id: '6003349442621', name: 'Gavekort & Shopping' }],
    excluded_zips: [],
    qualifying_questions: [
      {
        key: 'current_operator',
        label: 'Hvilken mobiloperatør har du i dag?',
        options: ['OneCall / Telia / Ice', 'Chili Mobil / Fjordkraft / Annet', 'Talkmore / Telenor / Bedrift'],
        disqualify_values: ['Talkmore / Telenor / Bedrift'],
      },
    ],
    sponsors: SPONSORS_TALKMORE,
    terms_draw_date: '31. desember 2026',
    show_on_offerwall: true,
    offerwall_priority: 12,
    status: 'active',
  },
  {
    comp_key: 'no_talkmore_tui_114903',
    cloak_slug: 'talkmore-tui',
    provider_network: 'SmartResponse',
    start_date_iso: '2026-09-17',
    end_date_iso: '2026-11-19',
    paid_lead_age_range: '25–70',
    sr_campaign_id: '114903',
    sr_affiliate_id: '1336',
    geo: 'no',
    category: 'travel',
    title: 'Talkmore: Vinn reisegavekort hos TUI (Direkte)',
    subtitle: 'Drømmer du om sol og varme? Delta i trekningen om et TUI reisegavekort med Talkmore (40 NOK / 26,40 DKK).',
    prize_name: 'TUI Reisegavekort 5.000 kr (Talkmore)',
    prize_value_dkk: 5000,
    payout_dkk: 26.4,
    tracking_url_template: 'https://ms.svarnorge.no/Product.aspx?productId=114903&ui=12449&inc=TUI&groupId=1486&offerId=#oid#&affiliateId=#affid#&requestid=#reqid#&subid1=#s1#&subid2=#s2#&utm_source=smr&utm_medium=affiliate&utm_campaign=talkmore',
    exclusion_pools: ['pool_no_talkmore', 'pool_global_blacklist'],
    target_age_min: 25,
    target_age_max: 65,
    target_genders: [],
    target_interests: [{ id: '6003139266461', name: 'Reiser & TUI' }],
    excluded_zips: [],
    qualifying_questions: [
      {
        key: 'current_operator',
        label: 'Hvilken mobiloperatør har du i dag?',
        options: ['OneCall / Telia / Ice', 'Chili Mobil / Fjordkraft / Annet', 'Talkmore / Telenor / Bedrift'],
        disqualify_values: ['Talkmore / Telenor / Bedrift'],
      },
    ],
    sponsors: SPONSORS_TALKMORE,
    terms_draw_date: '31. desember 2026',
    show_on_offerwall: true,
    offerwall_priority: 15,
    status: 'active',
  },
  {
    comp_key: 'no_talkmore_iskremmaskin_114903',
    cloak_slug: 'talkmore-iskrem',
    provider_network: 'SmartResponse',
    start_date_iso: '2026-09-17',
    end_date_iso: '2026-11-19',
    paid_lead_age_range: '25–70',
    sr_campaign_id: '114903',
    sr_affiliate_id: '1336',
    geo: 'no',
    category: 'electronics',
    title: 'Talkmore: Vinn en eksklusiv Iskremmaskin (Direkte)',
    subtitle: 'Lag deilig hjemmelaget iskrem på minutter! Gi samtykke til Talkmore for å delta (40 NOK / 26,40 DKK).',
    prize_name: 'Iskremmaskin (Talkmore)',
    prize_value_dkk: 3000,
    payout_dkk: 26.4,
    tracking_url_template: 'https://ms.svarnorge.no/Product.aspx?productId=114903&ui=12451&inc=Iskremmaskin&groupId=1486&offerId=#oid#&affiliateId=#affid#&requestid=#reqid#&subid1=#s1#&subid2=#s2#&utm_source=smr&utm_medium=affiliate&utm_campaign=talkmore',
    exclusion_pools: ['pool_no_talkmore', 'pool_global_blacklist'],
    target_age_min: 25,
    target_age_max: 65,
    target_genders: [],
    target_interests: [{ id: '6003195796498', name: 'Kjøkken & Hjem' }],
    excluded_zips: [],
    qualifying_questions: [
      {
        key: 'current_operator',
        label: 'Hvilken mobiloperatør har du i dag?',
        options: ['OneCall / Telia / Ice', 'Chili Mobil / Fjordkraft / Annet', 'Talkmore / Telenor / Bedrift'],
        disqualify_values: ['Talkmore / Telenor / Bedrift'],
      },
    ],
    sponsors: SPONSORS_TALKMORE,
    terms_draw_date: '31. desember 2026',
    show_on_offerwall: true,
    offerwall_priority: 20,
    status: 'active',
  },
  {
    comp_key: 'no_onecall_gogift_114889',
    cloak_slug: 'onecall-gogift',
    provider_network: 'SmartResponse',
    start_date_iso: '2026-09-03',
    end_date_iso: '2026-12-08',
    paid_lead_age_range: '25–70',
    sr_campaign_id: '114889',
    sr_affiliate_id: '1336',
    geo: 'no',
    category: 'grocery',
    title: 'OneCall: Vinn GoGift Supergavekort (Direkte)',
    subtitle: 'Delta i trekningen om et GoGift gavekort med OneCall (32 NOK / 21,10 DKK).',
    prize_name: 'GoGift Gavekort 10.000 kr (OneCall)',
    prize_value_dkk: 10000,
    payout_dkk: 21.1,
    tracking_url_template: 'https://ms.svarnorge.no/Product.aspx?productId=114889&offerId=#oid#&affiliateId=#affid#&requestid=#reqid#&subid1=#s1#&subid2=#s2#',
    exclusion_pools: ['pool_no_onecall', 'pool_global_blacklist'],
    target_age_min: 25,
    target_age_max: 65,
    target_genders: [],
    target_interests: [{ id: '6003349442621', name: 'Gavekort & Shopping' }],
    excluded_zips: [],
    qualifying_questions: [
      {
        key: 'current_operator',
        label: 'Hvilken mobiloperatør har du i dag?',
        options: ['Telenor', 'Talkmore', 'Ice', 'OneCall / Telia / MyCall'],
        disqualify_values: ['OneCall / Telia / MyCall'],
      },
      {
        key: 'company_phone',
        label: 'Har du firmabetalt mobilabonnement?',
        options: ['Nei (Privat mobil)', 'Ja (Firmatelefon)'],
        disqualify_values: ['Ja (Firmatelefon)'],
      },
    ],
    sponsors: SPONSORS_ONECALL,
    terms_draw_date: '31. desember 2026',
    show_on_offerwall: true,
    offerwall_priority: 30,
    status: 'active',
  },
  {
    comp_key: 'no_onecall_ving_114874',
    cloak_slug: 'onecall-ving',
    provider_network: 'SmartResponse',
    start_date_iso: '2026-08-26',
    end_date_iso: '2026-11-30',
    paid_lead_age_range: '25–70',
    sr_campaign_id: '114874',
    sr_affiliate_id: '1336',
    geo: 'no',
    category: 'travel',
    title: 'OneCall: Vinn reisegavekort hos VING (Direkte)',
    subtitle: 'Vinn et VING reisegavekort til din neste drømmeferie med OneCall (32 NOK / 21,10 DKK).',
    prize_name: 'VING Reisegavekort 10.000 kr (OneCall)',
    prize_value_dkk: 10000,
    payout_dkk: 21.1,
    tracking_url_template: 'https://ms.svarnorge.no/Product.aspx?productId=114874&offerId=#oid#&affiliateId=#affid#&requestid=#reqid#&subid1=#s1#&subid2=#s2#',
    exclusion_pools: ['pool_no_onecall', 'pool_global_blacklist'],
    target_age_min: 25,
    target_age_max: 65,
    target_genders: [],
    target_interests: [{ id: '6003139266461', name: 'Reiser & VING' }],
    excluded_zips: [],
    qualifying_questions: [
      {
        key: 'current_operator',
        label: 'Hvilken mobiloperatør har du i dag?',
        options: ['Telenor', 'Talkmore', 'Ice', 'OneCall / Telia / MyCall'],
        disqualify_values: ['OneCall / Telia / MyCall'],
      },
      {
        key: 'company_phone',
        label: 'Har du firmabetalt mobilabonnement?',
        options: ['Nei (Privat mobil)', 'Ja (Firmatelefon)'],
        disqualify_values: ['Ja (Firmatelefon)'],
      },
    ],
    sponsors: SPONSORS_ONECALL,
    terms_draw_date: '31. desember 2026',
    show_on_offerwall: true,
    offerwall_priority: 35,
    status: 'active',
  },
  {
    comp_key: 'dk_eforbruger_apollo_114919',
    cloak_slug: 'eforbruger-apollo',
    provider_network: 'SmartResponse',
    start_date_iso: '2026-09-30',
    end_date_iso: '2027-01-06',
    paid_lead_age_range: '25–70',
    sr_campaign_id: '114919',
    sr_affiliate_id: '1336',
    geo: 'dk',
    category: 'travel',
    title: 'E-forbruger: Vind et Apollo Rejsegavekort (Leadshare)',
    subtitle: 'Svar på dagens spørgsmål og deltag i konkurrencen om et gavekort på 5.000 kr. til Apollo Rejser (Payout: 32 DKK).',
    prize_name: 'Apollo Rejsegavekort 5.000 kr.',
    prize_value_dkk: 5000,
    payout_dkk: 32.0,
    tracking_url_template: 'https://ms.e-forbruger.dk/Product.aspx?productId=114919&offerId=#oid#&affiliateId=#affid#&requestid=#reqid#&subid1=#s1#&subid2=#s2#&groupId=1411',
    exclusion_pools: ['pool_dk_eforbruger', 'pool_global_blacklist'],
    target_age_min: 25,
    target_age_max: 65,
    target_genders: [],
    target_interests: [{ id: '6003139266461', name: 'Rejser & Charterferie' }],
    excluded_zips: [],
    qualifying_questions: [
      {
        key: 'age_segment',
        label: 'Hvor gammel er du?',
        options: ['25-34 år', '35-49 år', '50+ år', 'Under 25 år'],
        disqualify_values: ['Under 25 år'],
      },
    ],
    sponsors: SPONSORS_EFORBRUGER,
    terms_draw_date: '31. december 2026',
    show_on_offerwall: true,
    offerwall_priority: 10,
    status: 'active',
  },
  {
    comp_key: 'dk_tryg_silvan_114888',
    cloak_slug: 'tryg-silvan',
    provider_network: 'SmartResponse',
    start_date_iso: '2026-09-07',
    end_date_iso: '2026-12-07',
    paid_lead_age_range: '25–58',
    sr_campaign_id: '114888',
    sr_affiliate_id: '1336',
    geo: 'dk',
    category: 'home',
    title: 'Tryg: Vind 10.000 kr. gavekort til Silvan (Standalone)',
    subtitle: 'Opgrader hus og have med 10.000 kr. til Silvan! Giv samtykke til uforpligtende kontakt fra Tryg Forsikring (Payout: 22 DKK).',
    prize_name: 'Silvan Gavekort 10.000 kr. (Tryg)',
    prize_value_dkk: 10000,
    payout_dkk: 22.0,
    tracking_url_template: 'https://ms.svardanmark.dk/Product.aspx?productId=114888&offerId=#oid#&affiliateId=#affid#&requestid=#reqid#&subid1=#s1#&subid2=#s2#',
    exclusion_pools: ['pool_dk_tryg', 'pool_global_blacklist'],
    target_age_min: 25,
    target_age_max: 58,
    target_genders: [],
    target_interests: [{ id: '6003152672814', name: 'Boligejere, Hus & Have' }],
    excluded_zips: [],
    qualifying_questions: [
      {
        key: 'current_insurer',
        label: 'Er du allerede kunde hos Tryg Forsikring i dag?',
        options: ['Nej, jeg har et andet selskab', 'Ja, jeg har allerede Tryg'],
        disqualify_values: ['Ja, jeg har allerede Tryg'],
      },
    ],
    sponsors: SPONSORS_TRYG,
    terms_draw_date: '31. december 2026',
    show_on_offerwall: true,
    offerwall_priority: 20,
    status: 'active',
  },
  {
    comp_key: 'dk_ase_apollo_114875',
    cloak_slug: 'ase-apollo',
    provider_network: 'SmartResponse',
    start_date_iso: '2026-08-26',
    end_date_iso: '2026-11-30',
    paid_lead_age_range: '25–58',
    sr_campaign_id: '114875',
    sr_affiliate_id: '1336',
    geo: 'dk',
    category: 'travel',
    title: 'ASE: Vind 5.000 kr. til Apollo Rejser (Standalone)',
    subtitle: 'Vind 5.000 kr. til din næste ferie med Apollo! Giv samtykke til markedsføring fra Ase for at deltage (Payout: 22 DKK).',
    prize_name: 'Apollo Rejsegavekort 5.000 kr. (ASE)',
    prize_value_dkk: 5000,
    payout_dkk: 22.0,
    tracking_url_template: 'https://ms.svardanmark.dk/Product.aspx?productId=114875&offerId=#oid#&affiliateId=#affid#&requestid=#reqid#&subid1=#s1#&subid2=#s2#',
    exclusion_pools: ['pool_dk_ase', 'pool_global_blacklist'],
    target_age_min: 25,
    target_age_max: 58,
    target_genders: [],
    target_interests: [{ id: '6003139266461', name: 'Rejser, Lønmodtagere & Selvstændige' }],
    excluded_zips: [],
    qualifying_questions: [
      {
        key: 'current_akasse',
        label: 'Hvad er din nuværende beskæftigelse og a-kasse?',
        options: ['Lønmodtager / Selvstændig (Ikke Ase)', 'Allerede medlem af Ase', 'Arbejdsløs / Flexjob / Studerende'],
        disqualify_values: ['Allerede medlem af Ase', 'Arbejdsløs / Flexjob / Studerende'],
      },
    ],
    sponsors: SPONSORS_ASE,
    terms_draw_date: '31. december 2026',
    show_on_offerwall: true,
    offerwall_priority: 30,
    status: 'active',
  },

  {
    comp_key: "no_talkmore_babyutstyr_114903",
    cloak_slug: "talkmore-babyutstyr",
    provider_network: "SmartResponse",
    start_date_iso: "2026-09-17",
    end_date_iso: "2026-11-19",
    paid_lead_age_range: "25–70",
    sr_campaign_id: "114903",
    sr_affiliate_id: "1336",
    geo: "no",
    category: "family",
    title: "Talkmore: Vinn gavekort til babyutstyr på 10.000 kr. (Direkte)",
    subtitle: "Vinn 10.000 kr. til valgfritt babyutstyr og barneprodukter! Gi samtykke til et godt tilbud fra Talkmore for å delta (40 NOK payout).",
    prize_name: "Babyutstyr Gavekort 10.000 kr. (Talkmore)",
    prize_value_dkk: 10000.0,
    payout_dkk: 26.4,
    tracking_url_template: "https://ms.svarnorge.no/Product.aspx?productId=114903&ui=12450&inc=Babyutstyr&groupId=1486&offerId=#oid#&affiliateId=#affid#&requestid=#reqid#&subid1=#s1#&subid2=#s2#&utm_source=smr&utm_medium=affiliate&utm_campaign=talkmore",
    exclusion_pools: ["pool_no_talkmore", "pool_global_blacklist"],
    target_age_min: 25,
    target_age_max: 65,
    target_genders: [],
    target_interests: [{"id": "6003349442621", "name": "Småbarnsforeldre & Babyutstyr"}],
    excluded_zips: [],
    qualifying_questions: [{"key": "current_operator", "label": "Nåværende mobiloperatør", "options": ["Telia", "OneCall", "Ice", "Annet", "Talkmore", "Telenor", "Bedriftsabonnement"], "disqualify_values": ["Talkmore", "Telenor", "Bedriftsabonnement"]}],
    sponsors: SPONSORS_TALKMORE,
    terms_draw_date: "19. november 2026",
    show_on_offerwall: true,
    offerwall_priority: 22,
    status: "active",
  },
  {
    comp_key: "no_talkmore_gogift_meisel_114904",
    cloak_slug: "talkmore-gogift-meisel",
    provider_network: "SmartResponse",
    start_date_iso: "2026-09-17",
    end_date_iso: "2026-11-19",
    paid_lead_age_range: "25–70",
    sr_campaign_id: "114904",
    sr_affiliate_id: "1336",
    geo: "no",
    category: "grocery",
    title: "Talkmore: Vinn SuperGavekort fra GoGift på 10.000 kr. (Meisel)",
    subtitle: "Delta i trekningen om et GoGift SuperGavekort på 10.000 kr. (Meisel-flow). Gi samtykke til Talkmore for å delta (20 NOK payout).",
    prize_name: "GoGift 10.000 kr. (Talkmore Meisel)",
    prize_value_dkk: 10000.0,
    payout_dkk: 13.2,
    tracking_url_template: "https://ms.svarnorge.no/Product.aspx?productId=114904&offerId=#oid#&affiliateId=#affid#&requestid=#reqid#&subid1=#s1#&subid2=#s2#&utm_source=smr&utm_medium=affiliate&utm_campaign=talkmore",
    exclusion_pools: ["pool_no_talkmore", "pool_global_blacklist"],
    target_age_min: 25,
    target_age_max: 65,
    target_genders: [],
    target_interests: [{"id": "6003349442621", "name": "Gavekort & Shopping"}],
    excluded_zips: [],
    qualifying_questions: [{"key": "current_operator", "label": "Nåværende mobiloperatør", "options": ["Telia", "OneCall", "Ice", "Annet", "Talkmore", "Telenor", "Bedriftsabonnement"], "disqualify_values": ["Talkmore", "Telenor", "Bedriftsabonnement"]}],
    sponsors: SPONSORS_TALKMORE,
    terms_draw_date: "19. november 2026",
    show_on_offerwall: true,
    offerwall_priority: 36,
    status: "active",
  },
  {
    comp_key: "no_talkmore_tui_meisel_114904",
    cloak_slug: "talkmore-tui-meisel",
    provider_network: "SmartResponse",
    start_date_iso: "2026-09-17",
    end_date_iso: "2026-11-19",
    paid_lead_age_range: "25–70",
    sr_campaign_id: "114904",
    sr_affiliate_id: "1336",
    geo: "no",
    category: "travel",
    title: "Talkmore: Vinn reisegavekort hos TUI på 10.000 kr. (Meisel)",
    subtitle: "Vinn 10.000 kr. til sol og ferie med TUI (Meisel-flow). Gi samtykke til Talkmore for å delta (20 NOK payout).",
    prize_name: "TUI Reisegavekort 10.000 kr. (Talkmore Meisel)",
    prize_value_dkk: 10000.0,
    payout_dkk: 13.2,
    tracking_url_template: "https://ms.svarnorge.no/Product.aspx?productId=114904&ui=12449&inc=TUI&offerId=#oid#&affiliateId=#affid#&requestid=#reqid#&subid1=#s1#&subid2=#s2#&utm_source=smr&utm_medium=affiliate&utm_campaign=talkmore",
    exclusion_pools: ["pool_no_talkmore", "pool_global_blacklist"],
    target_age_min: 25,
    target_age_max: 65,
    target_genders: [],
    target_interests: [{"id": "6003139266461", "name": "Reiser & Syden"}],
    excluded_zips: [],
    qualifying_questions: [{"key": "current_operator", "label": "Nåværende mobiloperatør", "options": ["Telia", "OneCall", "Ice", "Annet", "Talkmore", "Telenor", "Bedriftsabonnement"], "disqualify_values": ["Talkmore", "Telenor", "Bedriftsabonnement"]}],
    sponsors: SPONSORS_TALKMORE,
    terms_draw_date: "19. november 2026",
    show_on_offerwall: true,
    offerwall_priority: 37,
    status: "active",
  },
  {
    comp_key: "no_talkmore_babyutstyr_meisel_114904",
    cloak_slug: "talkmore-baby-meisel",
    provider_network: "SmartResponse",
    start_date_iso: "2026-09-17",
    end_date_iso: "2026-11-19",
    paid_lead_age_range: "25–70",
    sr_campaign_id: "114904",
    sr_affiliate_id: "1336",
    geo: "no",
    category: "family",
    title: "Talkmore: Vinn 10.000 kr. til babyutstyr (Meisel)",
    subtitle: "Delta i trekningen om 10.000 kr. til babyutstyr (Meisel-flow). Gi samtykke til Talkmore for å delta (20 NOK payout).",
    prize_name: "Babyutstyr 10.000 kr. (Talkmore Meisel)",
    prize_value_dkk: 10000.0,
    payout_dkk: 13.2,
    tracking_url_template: "https://ms.svarnorge.no/Product.aspx?productId=114904&ui=12450&inc=Babyutstyr&offerId=#oid#&affiliateId=#affid#&requestid=#reqid#&subid1=#s1#&subid2=#s2#&utm_source=smr&utm_medium=affiliate&utm_campaign=talkmore",
    exclusion_pools: ["pool_no_talkmore", "pool_global_blacklist"],
    target_age_min: 25,
    target_age_max: 65,
    target_genders: [],
    target_interests: [{"id": "6003349442621", "name": "Familie & Baby"}],
    excluded_zips: [],
    qualifying_questions: [{"key": "current_operator", "label": "Nåværende mobiloperatør", "options": ["Telia", "OneCall", "Ice", "Annet", "Talkmore", "Telenor", "Bedriftsabonnement"], "disqualify_values": ["Talkmore", "Telenor", "Bedriftsabonnement"]}],
    sponsors: SPONSORS_TALKMORE,
    terms_draw_date: "19. november 2026",
    show_on_offerwall: true,
    offerwall_priority: 38,
    status: "active",
  },
  {
    comp_key: "no_onecall_rema1000_orderfeed_141526",
    cloak_slug: "onecall-rema-orderfeed",
    provider_network: "SmartResponse",
    start_date_iso: "2026-10-02",
    end_date_iso: "2026-12-22",
    paid_lead_age_range: "25–70",
    sr_campaign_id: "114927",
    sr_affiliate_id: "15490",
    geo: "no",
    category: "grocery",
    title: "OneCall: Vinn REMA 1000 SuperGavekort (Dedikert Orderfeed #141526)",
    subtitle: "Dedikert Cake Orderfeed-kontrakt (Offer 141526 / Camp 354566) for direkte API-levering til OneCall REMA 1000 (32 NOK payout).",
    prize_name: "REMA 1000 SuperGavekort 10.988 kr. (Orderfeed)",
    prize_value_dkk: 10988.0,
    payout_dkk: 21.1,
    tracking_url_template: "https://secure.smartresponse-media.com/?a=15490&c=354566&s1=#s1#&s2=#s2#",
    exclusion_pools: ["pool_no_onecall", "pool_global_blacklist"],
    target_age_min: 25,
    target_age_max: 65,
    target_genders: [],
    target_interests: [{"id": "6003349442621", "name": "Dagligvarer & REMA 1000"}],
    excluded_zips: [],
    qualifying_questions: [{"key": "current_operator", "label": "Nåværende mobiloperatør", "options": ["Talkmore", "Telenor", "Ice", "Annet", "Telia", "OneCall", "MyCall", "Bedriftsabonnement"], "disqualify_values": ["OneCall", "Telia", "MyCall", "Bedriftsabonnement"]}],
    sponsors: SPONSORS_ONECALL,
    terms_draw_date: "22. desember 2026",
    show_on_offerwall: false,
    offerwall_priority: 39,
    status: "active",
  },
  {
    comp_key: "no_northguard_drivhus_114805",
    cloak_slug: "northguard-drivhus",
    provider_network: "SmartResponse",
    start_date_iso: "2026-07-20",
    end_date_iso: "2026-09-21",
    paid_lead_age_range: "40–75",
    sr_campaign_id: "114805",
    sr_affiliate_id: "1336",
    geo: "no",
    category: "home",
    title: "Northguard: Vinn et drivhus / GoGift gavekort på 3.500 kr.",
    subtitle: "Avsluttet konkurranse i samarbeid med Northguard Norge AS (løp fra 20. juli til 21. september 2026).",
    prize_name: "Drivhus / GoGift 3.500 kr. (Northguard)",
    prize_value_dkk: 3500.0,
    payout_dkk: 19.8,
    tracking_url_template: "https://ms.svarnorge.no/Product.aspx?productId=114805&offerId=#oid#&affiliateId=#affid#&requestid=#reqid#&subid1=#s1#&subid2=#s2#",
    exclusion_pools: ["pool_no_other_sr", "pool_global_blacklist"],
    target_age_min: 40,
    target_age_max: 75,
    target_genders: [],
    target_interests: [],
    excluded_zips: [],
    qualifying_questions: [],
    sponsors: SPONSORS_NORTHGUARD,
    terms_draw_date: "21. september 2026",
    show_on_offerwall: false,
    offerwall_priority: 90,
    status: "expired",
  },
  {
    comp_key: "no_allente_tvpakke_114764",
    cloak_slug: "allente-tvpakke",
    provider_network: "SmartResponse",
    start_date_iso: "2026-06-01",
    end_date_iso: "2026-08-31",
    paid_lead_age_range: "30–70",
    sr_campaign_id: "114764",
    sr_affiliate_id: "1336",
    geo: "no",
    category: "electronics",
    title: "Allente: Vinn et Premium TV- og strømmeabonnement",
    subtitle: "Avsluttet konkurranse i samarbeid med Allente Norge AS (30–70 år, ikke eksisterende Allente-kunde).",
    prize_name: "Allente Premium-abonnement (5.000 kr.)",
    prize_value_dkk: 5000.0,
    payout_dkk: 23.1,
    tracking_url_template: "https://ms.svarnorge.no/Product.aspx?productId=114764&offerId=#oid#&affiliateId=#affid#&requestid=#reqid#&subid1=#s1#&subid2=#s2#",
    exclusion_pools: ["pool_no_other_sr", "pool_global_blacklist"],
    target_age_min: 30,
    target_age_max: 70,
    target_genders: [],
    target_interests: [],
    excluded_zips: [],
    qualifying_questions: [],
    sponsors: SPONSORS_ALLENTE,
    terms_draw_date: "31. august 2026",
    show_on_offerwall: false,
    offerwall_priority: 95,
    status: "expired",
  },
  {
    comp_key: "no_na_na_supermarked_20_20",
    cloak_slug: "na-supermarked-20",
    provider_network: "Nordic Affiliates",
    start_date_iso: "2026-08-15",
    end_date_iso: "2026-11-08",
    paid_lead_age_range: "18–75",
    sr_campaign_id: "20",
    sr_affiliate_id: "1020",
    geo: "no",
    category: "grocery",
    title: "Spareklubb Norge: Vinn et gavekort til dagligvarebutikken på 10.000 kr.",
    subtitle: "Delta gratis i trekningen om Dagligvare Gavekort 10.000 kr. (verdi 10.000 kr.). Tilbys av Spareklubb Norge / NorskeRabatter (3.50 EUR / 26.10 DKK CPL).",
    prize_name: "Dagligvare Gavekort 10.000 kr.",
    prize_value_dkk: 10000.0,
    payout_dkk: 26.1,
    tracking_url_template: "https://publicistlank.rabattbrev.com/aff_c?offer_id=20&aff_id=1020&aff_sub=#s1#&aff_sub2=#s2#",
    exclusion_pools: ["pool_no_spareklubben", "pool_global_blacklist"],
    target_age_min: 18,
    target_age_max: 75,
    target_genders: [],
    target_interests: [{"id": "6003349442621", "name": "Gavekort, Rabatter & Konkurranser"}],
    excluded_zips: [],
    qualifying_questions: [],
    sponsors: SPONSORS_SPAREKLUBBEN,
    terms_draw_date: "8. november 2026",
    show_on_offerwall: true,
    offerwall_priority: 40,
    status: "active",
  },
  {
    comp_key: "no_na_na_mummi_royaldesign_39",
    cloak_slug: "na-mummi-royaldesign",
    provider_network: "Nordic Affiliates",
    start_date_iso: "2026-08-15",
    end_date_iso: "2026-11-09",
    paid_lead_age_range: "18–75",
    sr_campaign_id: "39",
    sr_affiliate_id: "1020",
    geo: "no",
    category: "home",
    title: "Spareklubb Norge: Vinn Mummi-gavekort til Royal Design (5.000 kr.)",
    subtitle: "Delta gratis i trekningen om Mummi / Royal Design Gavekort 5.000 kr. (verdi 5.000 kr.). Tilbys av Spareklubb Norge / NorskeRabatter (3.50 EUR / 26.10 DKK CPL).",
    prize_name: "Mummi / Royal Design Gavekort 5.000 kr.",
    prize_value_dkk: 5000.0,
    payout_dkk: 26.1,
    tracking_url_template: "https://publicistlank.rabattbrev.com/aff_c?offer_id=39&aff_id=1020&aff_sub=#s1#&aff_sub2=#s2#",
    exclusion_pools: ["pool_no_spareklubben", "pool_global_blacklist"],
    target_age_min: 18,
    target_age_max: 75,
    target_genders: [],
    target_interests: [{"id": "6003349442621", "name": "Gavekort, Rabatter & Konkurranser"}],
    excluded_zips: [],
    qualifying_questions: [],
    sponsors: SPONSORS_SPAREKLUBBEN,
    terms_draw_date: "9. november 2026",
    show_on_offerwall: true,
    offerwall_priority: 41,
    status: "active",
  },
  {
    comp_key: "no_na_na_bensin_gavekort_40",
    cloak_slug: "na-bensin-gavekort",
    provider_network: "Nordic Affiliates",
    start_date_iso: "2026-08-15",
    end_date_iso: "2026-11-10",
    paid_lead_age_range: "18–75",
    sr_campaign_id: "40",
    sr_affiliate_id: "1020",
    geo: "no",
    category: "retail",
    title: "Spareklubb Norge: Vinn et drivstoff- og bensingavekort på 5.000 kr.",
    subtitle: "Delta gratis i trekningen om Bensingavekort 5.000 kr. (verdi 5.000 kr.). Tilbys av Spareklubb Norge / NorskeRabatter (3.50 EUR / 26.10 DKK CPL).",
    prize_name: "Bensingavekort 5.000 kr.",
    prize_value_dkk: 5000.0,
    payout_dkk: 26.1,
    tracking_url_template: "https://publicistlank.rabattbrev.com/aff_c?offer_id=40&aff_id=1020&aff_sub=#s1#&aff_sub2=#s2#",
    exclusion_pools: ["pool_no_spareklubben", "pool_global_blacklist"],
    target_age_min: 18,
    target_age_max: 75,
    target_genders: [],
    target_interests: [{"id": "6003349442621", "name": "Gavekort, Rabatter & Konkurranser"}],
    excluded_zips: [],
    qualifying_questions: [],
    sponsors: SPONSORS_SPAREKLUBBEN,
    terms_draw_date: "10. november 2026",
    show_on_offerwall: true,
    offerwall_priority: 42,
    status: "active",
  },
  {
    comp_key: "no_na_na_interior_royaldesign_50",
    cloak_slug: "na-interior-royaldesign",
    provider_network: "Nordic Affiliates",
    start_date_iso: "2026-08-20",
    end_date_iso: "2026-11-15",
    paid_lead_age_range: "18–75",
    sr_campaign_id: "50",
    sr_affiliate_id: "1020",
    geo: "no",
    category: "home",
    title: "Spareklubb Norge: Vinn interiørgavekort til Royal Design på 10.000 kr.",
    subtitle: "Delta gratis i trekningen om Interiør Gavekort 10.000 kr. (verdi 10.000 kr.). Tilbys av Spareklubb Norge / NorskeRabatter (3.50 EUR / 26.10 DKK CPL).",
    prize_name: "Interiør Gavekort 10.000 kr.",
    prize_value_dkk: 10000.0,
    payout_dkk: 26.1,
    tracking_url_template: "https://publicistlank.rabattbrev.com/aff_c?offer_id=50&aff_id=1020&aff_sub=#s1#&aff_sub2=#s2#",
    exclusion_pools: ["pool_no_spareklubben", "pool_global_blacklist"],
    target_age_min: 18,
    target_age_max: 75,
    target_genders: [],
    target_interests: [{"id": "6003349442621", "name": "Gavekort, Rabatter & Konkurranser"}],
    excluded_zips: [],
    qualifying_questions: [],
    sponsors: SPONSORS_SPAREKLUBBEN,
    terms_draw_date: "15. november 2026",
    show_on_offerwall: true,
    offerwall_priority: 43,
    status: "active",
  },
  {
    comp_key: "no_na_na_robotstovsuger_51",
    cloak_slug: "na-robotstovsuger",
    provider_network: "Nordic Affiliates",
    start_date_iso: "2026-08-20",
    end_date_iso: "2026-11-16",
    paid_lead_age_range: "18–75",
    sr_campaign_id: "51",
    sr_affiliate_id: "1020",
    geo: "no",
    category: "electronics",
    title: "Spareklubb Norge: Vinn en smart Robotstøvsuger (verdi 8.000 kr.)",
    subtitle: "Delta gratis i trekningen om Robotstøvsuger 8.000 kr. (verdi 8.000 kr.). Tilbys av Spareklubb Norge / NorskeRabatter (3.50 EUR / 26.10 DKK CPL).",
    prize_name: "Robotstøvsuger 8.000 kr.",
    prize_value_dkk: 8000.0,
    payout_dkk: 26.1,
    tracking_url_template: "https://publicistlank.rabattbrev.com/aff_c?offer_id=51&aff_id=1020&aff_sub=#s1#&aff_sub2=#s2#",
    exclusion_pools: ["pool_no_spareklubben", "pool_global_blacklist"],
    target_age_min: 18,
    target_age_max: 75,
    target_genders: [],
    target_interests: [{"id": "6003349442621", "name": "Gavekort, Rabatter & Konkurranser"}],
    excluded_zips: [],
    qualifying_questions: [],
    sponsors: SPONSORS_SPAREKLUBBEN,
    terms_draw_date: "16. november 2026",
    show_on_offerwall: true,
    offerwall_priority: 44,
    status: "active",
  },
  {
    comp_key: "no_na_na_delonghi_kaffequiz_53",
    cloak_slug: "na-delonghi-kaffequiz",
    provider_network: "Nordic Affiliates",
    start_date_iso: "2026-08-25",
    end_date_iso: "2026-11-20",
    paid_lead_age_range: "18–75",
    sr_campaign_id: "53",
    sr_affiliate_id: "1020",
    geo: "no",
    category: "electronics",
    title: "Spareklubb Norge: Vinn en DeLonghi Espressomaskin (Kaffequiz)",
    subtitle: "Delta gratis i trekningen om DeLonghi Kaffemaskin 7.500 kr. (verdi 7.500 kr.). Tilbys av Spareklubb Norge / NorskeRabatter (3.50 EUR / 26.10 DKK CPL).",
    prize_name: "DeLonghi Kaffemaskin 7.500 kr.",
    prize_value_dkk: 7500.0,
    payout_dkk: 26.1,
    tracking_url_template: "https://publicistlank.rabattbrev.com/aff_c?offer_id=53&aff_id=1020&aff_sub=#s1#&aff_sub2=#s2#",
    exclusion_pools: ["pool_no_spareklubben", "pool_global_blacklist"],
    target_age_min: 18,
    target_age_max: 75,
    target_genders: [],
    target_interests: [{"id": "6003349442621", "name": "Gavekort, Rabatter & Konkurranser"}],
    excluded_zips: [],
    qualifying_questions: [],
    sponsors: SPONSORS_SPAREKLUBBEN,
    terms_draw_date: "20. november 2026",
    show_on_offerwall: true,
    offerwall_priority: 45,
    status: "active",
  },
  {
    comp_key: "no_na_na_delonghi_direkte_54",
    cloak_slug: "na-delonghi-direkte",
    provider_network: "Nordic Affiliates",
    start_date_iso: "2026-08-25",
    end_date_iso: "2026-11-20",
    paid_lead_age_range: "18–75",
    sr_campaign_id: "54",
    sr_affiliate_id: "1020",
    geo: "no",
    category: "electronics",
    title: "Spareklubb Norge: Vinn en DeLonghi Kaffemaskin (Direkte påmelding)",
    subtitle: "Delta gratis i trekningen om DeLonghi Kaffemaskin 7.500 kr. (No PRL) (verdi 7.500 kr.). Tilbys av Spareklubb Norge / NorskeRabatter (3.50 EUR / 26.10 DKK CPL).",
    prize_name: "DeLonghi Kaffemaskin 7.500 kr. (No PRL)",
    prize_value_dkk: 7500.0,
    payout_dkk: 26.1,
    tracking_url_template: "https://publicistlank.rabattbrev.com/aff_c?offer_id=54&aff_id=1020&aff_sub=#s1#&aff_sub2=#s2#",
    exclusion_pools: ["pool_no_spareklubben", "pool_global_blacklist"],
    target_age_min: 18,
    target_age_max: 75,
    target_genders: [],
    target_interests: [{"id": "6003349442621", "name": "Gavekort, Rabatter & Konkurranser"}],
    excluded_zips: [],
    qualifying_questions: [],
    sponsors: SPONSORS_SPAREKLUBBEN,
    terms_draw_date: "20. november 2026",
    show_on_offerwall: true,
    offerwall_priority: 46,
    status: "active",
  },
  {
    comp_key: "no_na_na_supermarked_ny_67",
    cloak_slug: "na-supermarked-ny",
    provider_network: "Nordic Affiliates",
    start_date_iso: "2026-09-01",
    end_date_iso: "2026-11-25",
    paid_lead_age_range: "18–75",
    sr_campaign_id: "67",
    sr_affiliate_id: "1020",
    geo: "no",
    category: "grocery",
    title: "Spareklubb Norge: Vinn 10.000 kr. til valgfritt supermarked",
    subtitle: "Delta gratis i trekningen om Supermarked Gavekort 10.000 kr. (verdi 10.000 kr.). Tilbys av Spareklubb Norge / NorskeRabatter (3.50 EUR / 26.10 DKK CPL).",
    prize_name: "Supermarked Gavekort 10.000 kr.",
    prize_value_dkk: 10000.0,
    payout_dkk: 26.1,
    tracking_url_template: "https://publicistlank.rabattbrev.com/aff_c?offer_id=67&aff_id=1020&aff_sub=#s1#&aff_sub2=#s2#",
    exclusion_pools: ["pool_no_spareklubben", "pool_global_blacklist"],
    target_age_min: 18,
    target_age_max: 75,
    target_genders: [],
    target_interests: [{"id": "6003349442621", "name": "Gavekort, Rabatter & Konkurranser"}],
    excluded_zips: [],
    qualifying_questions: [],
    sponsors: SPONSORS_SPAREKLUBBEN,
    terms_draw_date: "25. november 2026",
    show_on_offerwall: true,
    offerwall_priority: 47,
    status: "active",
  },
  {
    comp_key: "no_na_na_bensin_direkte_73",
    cloak_slug: "na-bensin-direkte",
    provider_network: "Nordic Affiliates",
    start_date_iso: "2026-09-01",
    end_date_iso: "2026-11-25",
    paid_lead_age_range: "18–75",
    sr_campaign_id: "73",
    sr_affiliate_id: "1020",
    geo: "no",
    category: "retail",
    title: "Spareklubb Norge: Vinn bensingavekort på 5.000 kr. (Direkte)",
    subtitle: "Delta gratis i trekningen om Bensingavekort 5.000 kr. (No PRL) (verdi 5.000 kr.). Tilbys av Spareklubb Norge / NorskeRabatter (3.50 EUR / 26.10 DKK CPL).",
    prize_name: "Bensingavekort 5.000 kr. (No PRL)",
    prize_value_dkk: 5000.0,
    payout_dkk: 26.1,
    tracking_url_template: "https://publicistlank.rabattbrev.com/aff_c?offer_id=73&aff_id=1020&aff_sub=#s1#&aff_sub2=#s2#",
    exclusion_pools: ["pool_no_spareklubben", "pool_global_blacklist"],
    target_age_min: 18,
    target_age_max: 75,
    target_genders: [],
    target_interests: [{"id": "6003349442621", "name": "Gavekort, Rabatter & Konkurranser"}],
    excluded_zips: [],
    qualifying_questions: [],
    sponsors: SPONSORS_SPAREKLUBBEN,
    terms_draw_date: "25. november 2026",
    show_on_offerwall: true,
    offerwall_priority: 48,
    status: "active",
  },
  {
    comp_key: "no_na_na_universalgavekort_76_76",
    cloak_slug: "na-universalgavekort-76",
    provider_network: "Nordic Affiliates",
    start_date_iso: "2026-09-05",
    end_date_iso: "2026-11-28",
    paid_lead_age_range: "18–75",
    sr_campaign_id: "76",
    sr_affiliate_id: "1020",
    geo: "no",
    category: "retail",
    title: "Spareklubb Norge: Vinn et universalt SuperGavekort på 10.000 kr.",
    subtitle: "Delta gratis i trekningen om Universal Gavekort 10.000 kr. (verdi 10.000 kr.). Tilbys av Spareklubb Norge / NorskeRabatter (3.50 EUR / 26.10 DKK CPL).",
    prize_name: "Universal Gavekort 10.000 kr.",
    prize_value_dkk: 10000.0,
    payout_dkk: 26.1,
    tracking_url_template: "https://publicistlank.rabattbrev.com/aff_c?offer_id=76&aff_id=1020&aff_sub=#s1#&aff_sub2=#s2#",
    exclusion_pools: ["pool_no_spareklubben", "pool_global_blacklist"],
    target_age_min: 18,
    target_age_max: 75,
    target_genders: [],
    target_interests: [{"id": "6003349442621", "name": "Gavekort, Rabatter & Konkurranser"}],
    excluded_zips: [],
    qualifying_questions: [],
    sponsors: SPONSORS_SPAREKLUBBEN,
    terms_draw_date: "28. november 2026",
    show_on_offerwall: true,
    offerwall_priority: 49,
    status: "active",
  },
  {
    comp_key: "no_na_na_jula_gavekort_80",
    cloak_slug: "na-jula-gavekort",
    provider_network: "Nordic Affiliates",
    start_date_iso: "2026-09-05",
    end_date_iso: "2026-11-30",
    paid_lead_age_range: "18–75",
    sr_campaign_id: "80",
    sr_affiliate_id: "1020",
    geo: "no",
    category: "home",
    title: "Spareklubb Norge: Vinn et gavekort til Jula på 5.000 kr.",
    subtitle: "Delta gratis i trekningen om Jula Gavekort 5.000 kr. (verdi 5.000 kr.). Tilbys av Spareklubb Norge / NorskeRabatter (3.50 EUR / 26.10 DKK CPL).",
    prize_name: "Jula Gavekort 5.000 kr.",
    prize_value_dkk: 5000.0,
    payout_dkk: 26.1,
    tracking_url_template: "https://publicistlank.rabattbrev.com/aff_c?offer_id=80&aff_id=1020&aff_sub=#s1#&aff_sub2=#s2#",
    exclusion_pools: ["pool_no_spareklubben", "pool_global_blacklist"],
    target_age_min: 18,
    target_age_max: 75,
    target_genders: [],
    target_interests: [{"id": "6003349442621", "name": "Gavekort, Rabatter & Konkurranser"}],
    excluded_zips: [],
    qualifying_questions: [],
    sponsors: SPONSORS_SPAREKLUBBEN,
    terms_draw_date: "30. november 2026",
    show_on_offerwall: true,
    offerwall_priority: 50,
    status: "active",
  },
  {
    comp_key: "no_na_na_hm_gavekort_81",
    cloak_slug: "na-hm-gavekort",
    provider_network: "Nordic Affiliates",
    start_date_iso: "2026-09-05",
    end_date_iso: "2026-11-30",
    paid_lead_age_range: "18–75",
    sr_campaign_id: "81",
    sr_affiliate_id: "1020",
    geo: "no",
    category: "retail",
    title: "Spareklubb Norge: Vinn et gavekort til H&M på 10.000 kr.",
    subtitle: "Delta gratis i trekningen om H&M Gavekort 10.000 kr. (verdi 10.000 kr.). Tilbys av Spareklubb Norge / NorskeRabatter (3.50 EUR / 26.10 DKK CPL).",
    prize_name: "H&M Gavekort 10.000 kr.",
    prize_value_dkk: 10000.0,
    payout_dkk: 26.1,
    tracking_url_template: "https://publicistlank.rabattbrev.com/aff_c?offer_id=81&aff_id=1020&aff_sub=#s1#&aff_sub2=#s2#",
    exclusion_pools: ["pool_no_spareklubben", "pool_global_blacklist"],
    target_age_min: 18,
    target_age_max: 75,
    target_genders: [],
    target_interests: [{"id": "6003349442621", "name": "Gavekort, Rabatter & Konkurranser"}],
    excluded_zips: [],
    qualifying_questions: [],
    sponsors: SPONSORS_SPAREKLUBBEN,
    terms_draw_date: "30. november 2026",
    show_on_offerwall: true,
    offerwall_priority: 51,
    status: "active",
  },
  {
    comp_key: "no_na_na_xxl_gavekort_82",
    cloak_slug: "na-xxl-gavekort",
    provider_network: "Nordic Affiliates",
    start_date_iso: "2026-09-10",
    end_date_iso: "2026-12-01",
    paid_lead_age_range: "18–75",
    sr_campaign_id: "82",
    sr_affiliate_id: "1020",
    geo: "no",
    category: "retail",
    title: "Spareklubb Norge: Vinn et gavekort til XXL Sport & Villmark på 10.000 kr.",
    subtitle: "Delta gratis i trekningen om XXL Gavekort 10.000 kr. (verdi 10.000 kr.). Tilbys av Spareklubb Norge / NorskeRabatter (3.50 EUR / 26.10 DKK CPL).",
    prize_name: "XXL Gavekort 10.000 kr.",
    prize_value_dkk: 10000.0,
    payout_dkk: 26.1,
    tracking_url_template: "https://publicistlank.rabattbrev.com/aff_c?offer_id=82&aff_id=1020&aff_sub=#s1#&aff_sub2=#s2#",
    exclusion_pools: ["pool_no_spareklubben", "pool_global_blacklist"],
    target_age_min: 18,
    target_age_max: 75,
    target_genders: [],
    target_interests: [{"id": "6003349442621", "name": "Gavekort, Rabatter & Konkurranser"}],
    excluded_zips: [],
    qualifying_questions: [],
    sponsors: SPONSORS_SPAREKLUBBEN,
    terms_draw_date: "1. desember 2026",
    show_on_offerwall: true,
    offerwall_priority: 52,
    status: "active",
  },
  {
    comp_key: "no_na_na_ikea_gavekort_83",
    cloak_slug: "na-ikea-gavekort",
    provider_network: "Nordic Affiliates",
    start_date_iso: "2026-09-10",
    end_date_iso: "2026-12-01",
    paid_lead_age_range: "18–75",
    sr_campaign_id: "83",
    sr_affiliate_id: "1020",
    geo: "no",
    category: "home",
    title: "Spareklubb Norge: Vinn et gavekort til IKEA på 10.000 kr.",
    subtitle: "Delta gratis i trekningen om IKEA Gavekort 10.000 kr. (verdi 10.000 kr.). Tilbys av Spareklubb Norge / NorskeRabatter (3.50 EUR / 26.10 DKK CPL).",
    prize_name: "IKEA Gavekort 10.000 kr.",
    prize_value_dkk: 10000.0,
    payout_dkk: 26.1,
    tracking_url_template: "https://publicistlank.rabattbrev.com/aff_c?offer_id=83&aff_id=1020&aff_sub=#s1#&aff_sub2=#s2#",
    exclusion_pools: ["pool_no_spareklubben", "pool_global_blacklist"],
    target_age_min: 18,
    target_age_max: 75,
    target_genders: [],
    target_interests: [{"id": "6003349442621", "name": "Gavekort, Rabatter & Konkurranser"}],
    excluded_zips: [],
    qualifying_questions: [],
    sponsors: SPONSORS_SPAREKLUBBEN,
    terms_draw_date: "1. desember 2026",
    show_on_offerwall: true,
    offerwall_priority: 53,
    status: "active",
  },
  {
    comp_key: "no_na_na_elsykkel_91_91",
    cloak_slug: "na-elsykkel-91",
    provider_network: "Nordic Affiliates",
    start_date_iso: "2026-09-10",
    end_date_iso: "2026-12-05",
    paid_lead_age_range: "18–75",
    sr_campaign_id: "91",
    sr_affiliate_id: "1020",
    geo: "no",
    category: "electronics",
    title: "Spareklubb Norge: Vinn en Elsykkel (e-Bike) til verdi av 15.000 kr.",
    subtitle: "Delta gratis i trekningen om Elsykkel (e-Bike) 15.000 kr. (verdi 15.000 kr.). Tilbys av Spareklubb Norge / NorskeRabatter (3.50 EUR / 26.10 DKK CPL).",
    prize_name: "Elsykkel (e-Bike) 15.000 kr.",
    prize_value_dkk: 15000.0,
    payout_dkk: 26.1,
    tracking_url_template: "https://publicistlank.rabattbrev.com/aff_c?offer_id=91&aff_id=1020&aff_sub=#s1#&aff_sub2=#s2#",
    exclusion_pools: ["pool_no_spareklubben", "pool_global_blacklist"],
    target_age_min: 18,
    target_age_max: 75,
    target_genders: [],
    target_interests: [{"id": "6003349442621", "name": "Gavekort, Rabatter & Konkurranser"}],
    excluded_zips: [],
    qualifying_questions: [],
    sponsors: SPONSORS_SPAREKLUBBEN,
    terms_draw_date: "5. desember 2026",
    show_on_offerwall: true,
    offerwall_priority: 54,
    status: "active",
  },
  {
    comp_key: "no_na_na_norskerabatter_universal_111",
    cloak_slug: "na-norskerabatter-universal",
    provider_network: "Nordic Affiliates",
    start_date_iso: "2026-09-12",
    end_date_iso: "2026-12-05",
    paid_lead_age_range: "18–75",
    sr_campaign_id: "111",
    sr_affiliate_id: "1020",
    geo: "no",
    category: "retail",
    title: "NorskeRabatter: Vinn et universalt gavekort på 10.000 kr.",
    subtitle: "Delta gratis i trekningen om NorskeRabatter Gavekort 10.000 kr. (verdi 10.000 kr.). Tilbys av Spareklubb Norge / NorskeRabatter (3.50 EUR / 26.10 DKK CPL).",
    prize_name: "NorskeRabatter Gavekort 10.000 kr.",
    prize_value_dkk: 10000.0,
    payout_dkk: 26.1,
    tracking_url_template: "https://publicistlank.rabattbrev.com/aff_c?offer_id=111&aff_id=1020&aff_sub=#s1#&aff_sub2=#s2#",
    exclusion_pools: ["pool_no_spareklubben", "pool_global_blacklist"],
    target_age_min: 18,
    target_age_max: 75,
    target_genders: [],
    target_interests: [{"id": "6003349442621", "name": "Gavekort, Rabatter & Konkurranser"}],
    excluded_zips: [],
    qualifying_questions: [],
    sponsors: SPONSORS_SPAREKLUBBEN,
    terms_draw_date: "5. desember 2026",
    show_on_offerwall: true,
    offerwall_priority: 55,
    status: "active",
  },
  {
    comp_key: "no_na_na_norskerabatter_elsykkel_prl_113",
    cloak_slug: "na-norskerabatter-elsykkel-prl",
    provider_network: "Nordic Affiliates",
    start_date_iso: "2026-09-12",
    end_date_iso: "2026-12-08",
    paid_lead_age_range: "18–75",
    sr_campaign_id: "113",
    sr_affiliate_id: "1020",
    geo: "no",
    category: "electronics",
    title: "NorskeRabatter: Vinn en Elsykkel (med quiz-prelander)",
    subtitle: "Delta gratis i trekningen om Elsykkel 15.000 kr. (Prelander) (verdi 15.000 kr.). Tilbys av Spareklubb Norge / NorskeRabatter (3.50 EUR / 26.10 DKK CPL).",
    prize_name: "Elsykkel 15.000 kr. (Prelander)",
    prize_value_dkk: 15000.0,
    payout_dkk: 26.1,
    tracking_url_template: "https://publicistlank.rabattbrev.com/aff_c?offer_id=113&aff_id=1020&aff_sub=#s1#&aff_sub2=#s2#",
    exclusion_pools: ["pool_no_spareklubben", "pool_global_blacklist"],
    target_age_min: 18,
    target_age_max: 75,
    target_genders: [],
    target_interests: [{"id": "6003349442621", "name": "Gavekort, Rabatter & Konkurranser"}],
    excluded_zips: [],
    qualifying_questions: [],
    sponsors: SPONSORS_SPAREKLUBBEN,
    terms_draw_date: "8. desember 2026",
    show_on_offerwall: true,
    offerwall_priority: 56,
    status: "active",
  },
  {
    comp_key: "no_na_na_norskerabatter_elsykkel_114",
    cloak_slug: "na-norskerabatter-elsykkel",
    provider_network: "Nordic Affiliates",
    start_date_iso: "2026-09-12",
    end_date_iso: "2026-12-08",
    paid_lead_age_range: "18–75",
    sr_campaign_id: "114",
    sr_affiliate_id: "1020",
    geo: "no",
    category: "electronics",
    title: "NorskeRabatter: Vinn en Elsykkel (Direkte påmelding)",
    subtitle: "Delta gratis i trekningen om Elsykkel 15.000 kr. (Direkte) (verdi 15.000 kr.). Tilbys av Spareklubb Norge / NorskeRabatter (3.50 EUR / 26.10 DKK CPL).",
    prize_name: "Elsykkel 15.000 kr. (Direkte)",
    prize_value_dkk: 15000.0,
    payout_dkk: 26.1,
    tracking_url_template: "https://publicistlank.rabattbrev.com/aff_c?offer_id=114&aff_id=1020&aff_sub=#s1#&aff_sub2=#s2#",
    exclusion_pools: ["pool_no_spareklubben", "pool_global_blacklist"],
    target_age_min: 18,
    target_age_max: 75,
    target_genders: [],
    target_interests: [{"id": "6003349442621", "name": "Gavekort, Rabatter & Konkurranser"}],
    excluded_zips: [],
    qualifying_questions: [],
    sponsors: SPONSORS_SPAREKLUBBEN,
    terms_draw_date: "8. desember 2026",
    show_on_offerwall: true,
    offerwall_priority: 57,
    status: "active",
  },
  {
    comp_key: "no_na_na_norskerabatter_supermarked_115",
    cloak_slug: "na-norskerabatter-supermarked",
    provider_network: "Nordic Affiliates",
    start_date_iso: "2026-09-15",
    end_date_iso: "2026-12-10",
    paid_lead_age_range: "18–75",
    sr_campaign_id: "115",
    sr_affiliate_id: "1020",
    geo: "no",
    category: "grocery",
    title: "NorskeRabatter: Vinn 10.000 kr. til din favoritt matbutikk",
    subtitle: "Delta gratis i trekningen om Matbutikk Gavekort 10.000 kr. (verdi 10.000 kr.). Tilbys av Spareklubb Norge / NorskeRabatter (3.50 EUR / 26.10 DKK CPL).",
    prize_name: "Matbutikk Gavekort 10.000 kr.",
    prize_value_dkk: 10000.0,
    payout_dkk: 26.1,
    tracking_url_template: "https://publicistlank.rabattbrev.com/aff_c?offer_id=115&aff_id=1020&aff_sub=#s1#&aff_sub2=#s2#",
    exclusion_pools: ["pool_no_spareklubben", "pool_global_blacklist"],
    target_age_min: 18,
    target_age_max: 75,
    target_genders: [],
    target_interests: [{"id": "6003349442621", "name": "Gavekort, Rabatter & Konkurranser"}],
    excluded_zips: [],
    qualifying_questions: [],
    sponsors: SPONSORS_SPAREKLUBBEN,
    terms_draw_date: "10. desember 2026",
    show_on_offerwall: true,
    offerwall_priority: 58,
    status: "active",
  },
  {
    comp_key: "no_na_na_elkjop_gavekort_119",
    cloak_slug: "na-elkjop-gavekort",
    provider_network: "Nordic Affiliates",
    start_date_iso: "2026-09-15",
    end_date_iso: "2026-12-10",
    paid_lead_age_range: "18–75",
    sr_campaign_id: "119",
    sr_affiliate_id: "1020",
    geo: "no",
    category: "electronics",
    title: "NorskeRabatter: Vinn et gavekort til Elkjøp på 10.000 kr.",
    subtitle: "Delta gratis i trekningen om Elkjøp Gavekort 10.000 kr. (verdi 10.000 kr.). Tilbys av Spareklubb Norge / NorskeRabatter (3.50 EUR / 26.10 DKK CPL).",
    prize_name: "Elkjøp Gavekort 10.000 kr.",
    prize_value_dkk: 10000.0,
    payout_dkk: 26.1,
    tracking_url_template: "https://publicistlank.rabattbrev.com/aff_c?offer_id=119&aff_id=1020&aff_sub=#s1#&aff_sub2=#s2#",
    exclusion_pools: ["pool_no_spareklubben", "pool_global_blacklist"],
    target_age_min: 18,
    target_age_max: 75,
    target_genders: [],
    target_interests: [{"id": "6003349442621", "name": "Gavekort, Rabatter & Konkurranser"}],
    excluded_zips: [],
    qualifying_questions: [],
    sponsors: SPONSORS_SPAREKLUBBEN,
    terms_draw_date: "10. desember 2026",
    show_on_offerwall: true,
    offerwall_priority: 59,
    status: "active",
  },
  {
    comp_key: "no_na_na_vita_gavekort_120",
    cloak_slug: "na-vita-gavekort",
    provider_network: "Nordic Affiliates",
    start_date_iso: "2026-09-15",
    end_date_iso: "2026-12-12",
    paid_lead_age_range: "18–75",
    sr_campaign_id: "120",
    sr_affiliate_id: "1020",
    geo: "no",
    category: "retail",
    title: "NorskeRabatter: Vinn et gavekort til VITA på 5.000 kr.",
    subtitle: "Delta gratis i trekningen om VITA Gavekort 5.000 kr. (verdi 5.000 kr.). Tilbys av Spareklubb Norge / NorskeRabatter (3.50 EUR / 26.10 DKK CPL).",
    prize_name: "VITA Gavekort 5.000 kr.",
    prize_value_dkk: 5000.0,
    payout_dkk: 26.1,
    tracking_url_template: "https://publicistlank.rabattbrev.com/aff_c?offer_id=120&aff_id=1020&aff_sub=#s1#&aff_sub2=#s2#",
    exclusion_pools: ["pool_no_spareklubben", "pool_global_blacklist"],
    target_age_min: 18,
    target_age_max: 75,
    target_genders: [],
    target_interests: [{"id": "6003349442621", "name": "Gavekort, Rabatter & Konkurranser"}],
    excluded_zips: [],
    qualifying_questions: [],
    sponsors: SPONSORS_SPAREKLUBBEN,
    terms_draw_date: "12. desember 2026",
    show_on_offerwall: true,
    offerwall_priority: 60,
    status: "active",
  },
  {
    comp_key: "no_na_na_zara_gavekort_121",
    cloak_slug: "na-zara-gavekort",
    provider_network: "Nordic Affiliates",
    start_date_iso: "2026-09-15",
    end_date_iso: "2026-12-12",
    paid_lead_age_range: "18–75",
    sr_campaign_id: "121",
    sr_affiliate_id: "1020",
    geo: "no",
    category: "retail",
    title: "NorskeRabatter: Vinn et gavekort til ZARA på 10.000 kr.",
    subtitle: "Delta gratis i trekningen om ZARA Gavekort 10.000 kr. (verdi 10.000 kr.). Tilbys av Spareklubb Norge / NorskeRabatter (3.50 EUR / 26.10 DKK CPL).",
    prize_name: "ZARA Gavekort 10.000 kr.",
    prize_value_dkk: 10000.0,
    payout_dkk: 26.1,
    tracking_url_template: "https://publicistlank.rabattbrev.com/aff_c?offer_id=121&aff_id=1020&aff_sub=#s1#&aff_sub2=#s2#",
    exclusion_pools: ["pool_no_spareklubben", "pool_global_blacklist"],
    target_age_min: 18,
    target_age_max: 75,
    target_genders: [],
    target_interests: [{"id": "6003349442621", "name": "Gavekort, Rabatter & Konkurranser"}],
    excluded_zips: [],
    qualifying_questions: [],
    sponsors: SPONSORS_SPAREKLUBBEN,
    terms_draw_date: "12. desember 2026",
    show_on_offerwall: true,
    offerwall_priority: 61,
    status: "active",
  },
  {
    comp_key: "no_na_na_freia_sjokolade_134",
    cloak_slug: "na-freia-sjokolade",
    provider_network: "Nordic Affiliates",
    start_date_iso: "2026-09-18",
    end_date_iso: "2026-12-14",
    paid_lead_age_range: "18–75",
    sr_campaign_id: "134",
    sr_affiliate_id: "1020",
    geo: "no",
    category: "grocery",
    title: "Spareklubb Norge: Vinn en gigantisk Freia Sjokoladepakke & gavekort",
    subtitle: "Delta gratis i trekningen om Freia Sjokolade & Gavekort 5.000 kr. (verdi 5.000 kr.). Tilbys av Spareklubb Norge / NorskeRabatter (3.50 EUR / 26.10 DKK CPL).",
    prize_name: "Freia Sjokolade & Gavekort 5.000 kr.",
    prize_value_dkk: 5000.0,
    payout_dkk: 26.1,
    tracking_url_template: "https://publicistlank.rabattbrev.com/aff_c?offer_id=134&aff_id=1020&aff_sub=#s1#&aff_sub2=#s2#",
    exclusion_pools: ["pool_no_spareklubben", "pool_global_blacklist"],
    target_age_min: 18,
    target_age_max: 75,
    target_genders: [],
    target_interests: [{"id": "6003349442621", "name": "Gavekort, Rabatter & Konkurranser"}],
    excluded_zips: [],
    qualifying_questions: [],
    sponsors: SPONSORS_SPAREKLUBBEN,
    terms_draw_date: "14. desember 2026",
    show_on_offerwall: true,
    offerwall_priority: 62,
    status: "active",
  },
  {
    comp_key: "no_na_na_cubus_gavekort_142_142",
    cloak_slug: "na-cubus-gavekort-142",
    provider_network: "Nordic Affiliates",
    start_date_iso: "2026-09-18",
    end_date_iso: "2026-12-15",
    paid_lead_age_range: "18–75",
    sr_campaign_id: "142",
    sr_affiliate_id: "1020",
    geo: "no",
    category: "retail",
    title: "Spareklubb Norge: Vinn et gavekort til Cubus på 10.000 kr.",
    subtitle: "Delta gratis i trekningen om Cubus Gavekort 10.000 kr. (verdi 10.000 kr.). Tilbys av Spareklubb Norge / NorskeRabatter (3.50 EUR / 26.10 DKK CPL).",
    prize_name: "Cubus Gavekort 10.000 kr.",
    prize_value_dkk: 10000.0,
    payout_dkk: 26.1,
    tracking_url_template: "https://publicistlank.rabattbrev.com/aff_c?offer_id=142&aff_id=1020&aff_sub=#s1#&aff_sub2=#s2#",
    exclusion_pools: ["pool_no_spareklubben", "pool_global_blacklist"],
    target_age_min: 18,
    target_age_max: 75,
    target_genders: [],
    target_interests: [{"id": "6003349442621", "name": "Gavekort, Rabatter & Konkurranser"}],
    excluded_zips: [],
    qualifying_questions: [],
    sponsors: SPONSORS_SPAREKLUBBEN,
    terms_draw_date: "15. desember 2026",
    show_on_offerwall: true,
    offerwall_priority: 63,
    status: "active",
  },
  {
    comp_key: "no_na_na_carlings_gavekort_143",
    cloak_slug: "na-carlings-gavekort",
    provider_network: "Nordic Affiliates",
    start_date_iso: "2026-09-18",
    end_date_iso: "2026-12-15",
    paid_lead_age_range: "18–75",
    sr_campaign_id: "143",
    sr_affiliate_id: "1020",
    geo: "no",
    category: "retail",
    title: "Spareklubb Norge: Vinn et gavekort til Carlings på 10.000 kr.",
    subtitle: "Delta gratis i trekningen om Carlings Gavekort 10.000 kr. (verdi 10.000 kr.). Tilbys av Spareklubb Norge / NorskeRabatter (3.50 EUR / 26.10 DKK CPL).",
    prize_name: "Carlings Gavekort 10.000 kr.",
    prize_value_dkk: 10000.0,
    payout_dkk: 26.1,
    tracking_url_template: "https://publicistlank.rabattbrev.com/aff_c?offer_id=143&aff_id=1020&aff_sub=#s1#&aff_sub2=#s2#",
    exclusion_pools: ["pool_no_spareklubben", "pool_global_blacklist"],
    target_age_min: 18,
    target_age_max: 75,
    target_genders: [],
    target_interests: [{"id": "6003349442621", "name": "Gavekort, Rabatter & Konkurranser"}],
    excluded_zips: [],
    qualifying_questions: [],
    sponsors: SPONSORS_SPAREKLUBBEN,
    terms_draw_date: "15. desember 2026",
    show_on_offerwall: true,
    offerwall_priority: 64,
    status: "active",
  },
  {
    comp_key: "no_na_na_sport1_gavekort_144",
    cloak_slug: "na-sport1-gavekort",
    provider_network: "Nordic Affiliates",
    start_date_iso: "2026-09-20",
    end_date_iso: "2026-12-16",
    paid_lead_age_range: "18–75",
    sr_campaign_id: "144",
    sr_affiliate_id: "1020",
    geo: "no",
    category: "retail",
    title: "Spareklubb Norge: Vinn et gavekort til Sport 1 på 10.000 kr.",
    subtitle: "Delta gratis i trekningen om Sport 1 Gavekort 10.000 kr. (verdi 10.000 kr.). Tilbys av Spareklubb Norge / NorskeRabatter (3.50 EUR / 26.10 DKK CPL).",
    prize_name: "Sport 1 Gavekort 10.000 kr.",
    prize_value_dkk: 10000.0,
    payout_dkk: 26.1,
    tracking_url_template: "https://publicistlank.rabattbrev.com/aff_c?offer_id=144&aff_id=1020&aff_sub=#s1#&aff_sub2=#s2#",
    exclusion_pools: ["pool_no_spareklubben", "pool_global_blacklist"],
    target_age_min: 18,
    target_age_max: 75,
    target_genders: [],
    target_interests: [{"id": "6003349442621", "name": "Gavekort, Rabatter & Konkurranser"}],
    excluded_zips: [],
    qualifying_questions: [],
    sponsors: SPONSORS_SPAREKLUBBEN,
    terms_draw_date: "16. desember 2026",
    show_on_offerwall: true,
    offerwall_priority: 65,
    status: "active",
  },
  {
    comp_key: "no_na_na_apple_airpods_145",
    cloak_slug: "na-apple-airpods",
    provider_network: "Nordic Affiliates",
    start_date_iso: "2026-09-20",
    end_date_iso: "2026-12-16",
    paid_lead_age_range: "18–75",
    sr_campaign_id: "145",
    sr_affiliate_id: "1020",
    geo: "no",
    category: "electronics",
    title: "Spareklubb Norge: Vinn Apple AirPods Pro (verdi 3.500 kr.)",
    subtitle: "Delta gratis i trekningen om Apple AirPods Pro 3.500 kr. (verdi 3.500 kr.). Tilbys av Spareklubb Norge / NorskeRabatter (3.50 EUR / 26.10 DKK CPL).",
    prize_name: "Apple AirPods Pro 3.500 kr.",
    prize_value_dkk: 3500.0,
    payout_dkk: 26.1,
    tracking_url_template: "https://publicistlank.rabattbrev.com/aff_c?offer_id=145&aff_id=1020&aff_sub=#s1#&aff_sub2=#s2#",
    exclusion_pools: ["pool_no_spareklubben", "pool_global_blacklist"],
    target_age_min: 18,
    target_age_max: 75,
    target_genders: [],
    target_interests: [{"id": "6003349442621", "name": "Gavekort, Rabatter & Konkurranser"}],
    excluded_zips: [],
    qualifying_questions: [],
    sponsors: SPONSORS_SPAREKLUBBEN,
    terms_draw_date: "16. desember 2026",
    show_on_offerwall: true,
    offerwall_priority: 66,
    status: "active",
  },
  {
    comp_key: "no_na_na_garmin_polar_146",
    cloak_slug: "na-garmin-polar",
    provider_network: "Nordic Affiliates",
    start_date_iso: "2026-09-22",
    end_date_iso: "2026-12-18",
    paid_lead_age_range: "18–75",
    sr_campaign_id: "146",
    sr_affiliate_id: "1020",
    geo: "no",
    category: "electronics",
    title: "Spareklubb Norge: Vinn en Garmin / Polar treningsklokke (6.000 kr.)",
    subtitle: "Delta gratis i trekningen om Garmin / Polar Sportsklokke 6.000 kr. (verdi 6.000 kr.). Tilbys av Spareklubb Norge / NorskeRabatter (3.50 EUR / 26.10 DKK CPL).",
    prize_name: "Garmin / Polar Sportsklokke 6.000 kr.",
    prize_value_dkk: 6000.0,
    payout_dkk: 26.1,
    tracking_url_template: "https://publicistlank.rabattbrev.com/aff_c?offer_id=146&aff_id=1020&aff_sub=#s1#&aff_sub2=#s2#",
    exclusion_pools: ["pool_no_spareklubben", "pool_global_blacklist"],
    target_age_min: 18,
    target_age_max: 75,
    target_genders: [],
    target_interests: [{"id": "6003349442621", "name": "Gavekort, Rabatter & Konkurranser"}],
    excluded_zips: [],
    qualifying_questions: [],
    sponsors: SPONSORS_SPAREKLUBBEN,
    terms_draw_date: "18. desember 2026",
    show_on_offerwall: true,
    offerwall_priority: 67,
    status: "active",
  },
  {
    comp_key: "no_na_na_fischer_ski_147",
    cloak_slug: "na-fischer-ski",
    provider_network: "Nordic Affiliates",
    start_date_iso: "2026-09-22",
    end_date_iso: "2026-12-18",
    paid_lead_age_range: "18–75",
    sr_campaign_id: "147",
    sr_affiliate_id: "1020",
    geo: "no",
    category: "retail",
    title: "Spareklubb Norge: Vinn en komplett Fischer skipakke (8.000 kr.)",
    subtitle: "Delta gratis i trekningen om Fischer Skipakke 8.000 kr. (verdi 8.000 kr.). Tilbys av Spareklubb Norge / NorskeRabatter (3.50 EUR / 26.10 DKK CPL).",
    prize_name: "Fischer Skipakke 8.000 kr.",
    prize_value_dkk: 8000.0,
    payout_dkk: 26.1,
    tracking_url_template: "https://publicistlank.rabattbrev.com/aff_c?offer_id=147&aff_id=1020&aff_sub=#s1#&aff_sub2=#s2#",
    exclusion_pools: ["pool_no_spareklubben", "pool_global_blacklist"],
    target_age_min: 18,
    target_age_max: 75,
    target_genders: [],
    target_interests: [{"id": "6003349442621", "name": "Gavekort, Rabatter & Konkurranser"}],
    excluded_zips: [],
    qualifying_questions: [],
    sponsors: SPONSORS_SPAREKLUBBEN,
    terms_draw_date: "18. desember 2026",
    show_on_offerwall: true,
    offerwall_priority: 68,
    status: "active",
  },
  {
    comp_key: "no_na_na_omo_vaskepakke_148",
    cloak_slug: "na-omo-vaskepakke",
    provider_network: "Nordic Affiliates",
    start_date_iso: "2026-09-25",
    end_date_iso: "2026-12-20",
    paid_lead_age_range: "18–75",
    sr_campaign_id: "148",
    sr_affiliate_id: "1020",
    geo: "no",
    category: "grocery",
    title: "Spareklubb Norge: Vinn årsforbruk av OMO & dagligvare-gavekort (5.000 kr.)",
    subtitle: "Delta gratis i trekningen om OMO & Husholdningsgavekort 5.000 kr. (verdi 5.000 kr.). Tilbys av Spareklubb Norge / NorskeRabatter (3.50 EUR / 26.10 DKK CPL).",
    prize_name: "OMO & Husholdningsgavekort 5.000 kr.",
    prize_value_dkk: 5000.0,
    payout_dkk: 26.1,
    tracking_url_template: "https://publicistlank.rabattbrev.com/aff_c?offer_id=148&aff_id=1020&aff_sub=#s1#&aff_sub2=#s2#",
    exclusion_pools: ["pool_no_spareklubben", "pool_global_blacklist"],
    target_age_min: 18,
    target_age_max: 75,
    target_genders: [],
    target_interests: [{"id": "6003349442621", "name": "Gavekort, Rabatter & Konkurranser"}],
    excluded_zips: [],
    qualifying_questions: [],
    sponsors: SPONSORS_SPAREKLUBBEN,
    terms_draw_date: "20. desember 2026",
    show_on_offerwall: true,
    offerwall_priority: 69,
    status: "active",
  },
  {
    comp_key: "no_na_na_iphone_17_171",
    cloak_slug: "na-iphone-17",
    provider_network: "Nordic Affiliates",
    start_date_iso: "2026-10-01",
    end_date_iso: "2026-12-24",
    paid_lead_age_range: "18–75",
    sr_campaign_id: "171",
    sr_affiliate_id: "1020",
    geo: "no",
    category: "electronics",
    title: "Spareklubb Norge: Vinn en helt ny Apple iPhone 17 (Direkte)",
    subtitle: "Delta gratis i trekningen om Apple iPhone 17 (14.990 kr.) (verdi 14.990 kr.). Tilbys av Spareklubb Norge / NorskeRabatter (3.50 EUR / 26.10 DKK CPL).",
    prize_name: "Apple iPhone 17 (14.990 kr.)",
    prize_value_dkk: 14990.0,
    payout_dkk: 26.1,
    tracking_url_template: "https://publicistlank.rabattbrev.com/aff_c?offer_id=171&aff_id=1020&aff_sub=#s1#&aff_sub2=#s2#",
    exclusion_pools: ["pool_no_spareklubben", "pool_global_blacklist"],
    target_age_min: 18,
    target_age_max: 75,
    target_genders: [],
    target_interests: [{"id": "6003349442621", "name": "Gavekort, Rabatter & Konkurranser"}],
    excluded_zips: [],
    qualifying_questions: [],
    sponsors: SPONSORS_SPAREKLUBBEN,
    terms_draw_date: "24. desember 2026",
    show_on_offerwall: true,
    offerwall_priority: 70,
    status: "active",
  },
  {
    comp_key: "no_na_na_kicks_clarins_174",
    cloak_slug: "na-kicks-clarins",
    provider_network: "Nordic Affiliates",
    start_date_iso: "2026-10-01",
    end_date_iso: "2026-12-24",
    paid_lead_age_range: "18–75",
    sr_campaign_id: "174",
    sr_affiliate_id: "1020",
    geo: "no",
    category: "retail",
    title: "Spareklubb Norge: Vinn et gavekort til KICKS / Clarins på 10.000 kr.",
    subtitle: "Delta gratis i trekningen om KICKS / Clarins Gavekort 10.000 kr. (verdi 10.000 kr.). Tilbys av Spareklubb Norge / NorskeRabatter (3.50 EUR / 26.10 DKK CPL).",
    prize_name: "KICKS / Clarins Gavekort 10.000 kr.",
    prize_value_dkk: 10000.0,
    payout_dkk: 26.1,
    tracking_url_template: "https://publicistlank.rabattbrev.com/aff_c?offer_id=174&aff_id=1020&aff_sub=#s1#&aff_sub2=#s2#",
    exclusion_pools: ["pool_no_spareklubben", "pool_global_blacklist"],
    target_age_min: 18,
    target_age_max: 75,
    target_genders: [],
    target_interests: [{"id": "6003349442621", "name": "Gavekort, Rabatter & Konkurranser"}],
    excluded_zips: [],
    qualifying_questions: [],
    sponsors: SPONSORS_SPAREKLUBBEN,
    terms_draw_date: "24. desember 2026",
    show_on_offerwall: true,
    offerwall_priority: 71,
    status: "active",
  },
  {
    comp_key: "no_na_na_milka_10000_178",
    cloak_slug: "na-milka-10000",
    provider_network: "Nordic Affiliates",
    start_date_iso: "2026-10-05",
    end_date_iso: "2026-12-26",
    paid_lead_age_range: "18–75",
    sr_campaign_id: "178",
    sr_affiliate_id: "1020",
    geo: "no",
    category: "grocery",
    title: "Spareklubb Norge: Vinn Milka sjokolade & gavekort på 10.000 kr.",
    subtitle: "Delta gratis i trekningen om Milka & Gavekort 10.000 kr. (verdi 10.000 kr.). Tilbys av Spareklubb Norge / NorskeRabatter (3.50 EUR / 26.10 DKK CPL).",
    prize_name: "Milka & Gavekort 10.000 kr.",
    prize_value_dkk: 10000.0,
    payout_dkk: 26.1,
    tracking_url_template: "https://publicistlank.rabattbrev.com/aff_c?offer_id=178&aff_id=1020&aff_sub=#s1#&aff_sub2=#s2#",
    exclusion_pools: ["pool_no_spareklubben", "pool_global_blacklist"],
    target_age_min: 18,
    target_age_max: 75,
    target_genders: [],
    target_interests: [{"id": "6003349442621", "name": "Gavekort, Rabatter & Konkurranser"}],
    excluded_zips: [],
    qualifying_questions: [],
    sponsors: SPONSORS_SPAREKLUBBEN,
    terms_draw_date: "26. desember 2026",
    show_on_offerwall: true,
    offerwall_priority: 72,
    status: "active",
  },
  {
    comp_key: "no_na_na_rema_1000_180_180",
    cloak_slug: "na-rema-1000-180",
    provider_network: "Nordic Affiliates",
    start_date_iso: "2026-10-05",
    end_date_iso: "2026-12-26",
    paid_lead_age_range: "18–75",
    sr_campaign_id: "180",
    sr_affiliate_id: "1020",
    geo: "no",
    category: "grocery",
    title: "Spareklubb Norge: Vinn et gavekort til REMA 1000 på 10.000 kr.",
    subtitle: "Delta gratis i trekningen om REMA 1000 Gavekort 10.000 kr. (Spareklubb) (verdi 10.000 kr.). Tilbys av Spareklubb Norge / NorskeRabatter (3.50 EUR / 26.10 DKK CPL).",
    prize_name: "REMA 1000 Gavekort 10.000 kr. (Spareklubb)",
    prize_value_dkk: 10000.0,
    payout_dkk: 26.1,
    tracking_url_template: "https://publicistlank.rabattbrev.com/aff_c?offer_id=180&aff_id=1020&aff_sub=#s1#&aff_sub2=#s2#",
    exclusion_pools: ["pool_no_spareklubben", "pool_global_blacklist"],
    target_age_min: 18,
    target_age_max: 75,
    target_genders: [],
    target_interests: [{"id": "6003349442621", "name": "Gavekort, Rabatter & Konkurranser"}],
    excluded_zips: [],
    qualifying_questions: [],
    sponsors: SPONSORS_SPAREKLUBBEN,
    terms_draw_date: "26. desember 2026",
    show_on_offerwall: true,
    offerwall_priority: 73,
    status: "active",
  },
  {
    comp_key: "no_na_na_cocacola_5000_182",
    cloak_slug: "na-cocacola-5000",
    provider_network: "Nordic Affiliates",
    start_date_iso: "2026-10-08",
    end_date_iso: "2026-12-28",
    paid_lead_age_range: "18–75",
    sr_campaign_id: "182",
    sr_affiliate_id: "1020",
    geo: "no",
    category: "grocery",
    title: "Spareklubb Norge: Vinn Coca-Cola & gavekort på 5.000 kr.",
    subtitle: "Delta gratis i trekningen om Coca-Cola Gavekort 5.000 kr. (verdi 5.000 kr.). Tilbys av Spareklubb Norge / NorskeRabatter (3.50 EUR / 26.10 DKK CPL).",
    prize_name: "Coca-Cola Gavekort 5.000 kr.",
    prize_value_dkk: 5000.0,
    payout_dkk: 26.1,
    tracking_url_template: "https://publicistlank.rabattbrev.com/aff_c?offer_id=182&aff_id=1020&aff_sub=#s1#&aff_sub2=#s2#",
    exclusion_pools: ["pool_no_spareklubben", "pool_global_blacklist"],
    target_age_min: 18,
    target_age_max: 75,
    target_genders: [],
    target_interests: [{"id": "6003349442621", "name": "Gavekort, Rabatter & Konkurranser"}],
    excluded_zips: [],
    qualifying_questions: [],
    sponsors: SPONSORS_SPAREKLUBBEN,
    terms_draw_date: "28. desember 2026",
    show_on_offerwall: true,
    offerwall_priority: 74,
    status: "active",
  },
  {
    comp_key: "no_na_na_burger_king_185",
    cloak_slug: "na-burger-king",
    provider_network: "Nordic Affiliates",
    start_date_iso: "2026-10-08",
    end_date_iso: "2026-12-28",
    paid_lead_age_range: "18–75",
    sr_campaign_id: "185",
    sr_affiliate_id: "1020",
    geo: "no",
    category: "grocery",
    title: "Spareklubb Norge: Vinn et gavekort til Burger King på 5.000 kr.",
    subtitle: "Delta gratis i trekningen om Burger King Gavekort 5.000 kr. (verdi 5.000 kr.). Tilbys av Spareklubb Norge / NorskeRabatter (3.50 EUR / 26.10 DKK CPL).",
    prize_name: "Burger King Gavekort 5.000 kr.",
    prize_value_dkk: 5000.0,
    payout_dkk: 26.1,
    tracking_url_template: "https://publicistlank.rabattbrev.com/aff_c?offer_id=185&aff_id=1020&aff_sub=#s1#&aff_sub2=#s2#",
    exclusion_pools: ["pool_no_spareklubben", "pool_global_blacklist"],
    target_age_min: 18,
    target_age_max: 75,
    target_genders: [],
    target_interests: [{"id": "6003349442621", "name": "Gavekort, Rabatter & Konkurranser"}],
    excluded_zips: [],
    qualifying_questions: [],
    sponsors: SPONSORS_SPAREKLUBBEN,
    terms_draw_date: "28. desember 2026",
    show_on_offerwall: true,
    offerwall_priority: 75,
    status: "active",
  },
  {
    comp_key: "no_na_na_supermarked_kampanje_188",
    cloak_slug: "na-supermarked-kampanje",
    provider_network: "Nordic Affiliates",
    start_date_iso: "2026-10-10",
    end_date_iso: "2026-12-29",
    paid_lead_age_range: "18–75",
    sr_campaign_id: "188",
    sr_affiliate_id: "1020",
    geo: "no",
    category: "grocery",
    title: "Spareklubb Norge: Vinn 10.000 kr. til Norges beste dagligvarekjeder",
    subtitle: "Delta gratis i trekningen om Dagligvare Kampanje 10.000 kr. (verdi 10.000 kr.). Tilbys av Spareklubb Norge / NorskeRabatter (3.50 EUR / 26.10 DKK CPL).",
    prize_name: "Dagligvare Kampanje 10.000 kr.",
    prize_value_dkk: 10000.0,
    payout_dkk: 26.1,
    tracking_url_template: "https://publicistlank.rabattbrev.com/aff_c?offer_id=188&aff_id=1020&aff_sub=#s1#&aff_sub2=#s2#",
    exclusion_pools: ["pool_no_spareklubben", "pool_global_blacklist"],
    target_age_min: 18,
    target_age_max: 75,
    target_genders: [],
    target_interests: [{"id": "6003349442621", "name": "Gavekort, Rabatter & Konkurranser"}],
    excluded_zips: [],
    qualifying_questions: [],
    sponsors: SPONSORS_SPAREKLUBBEN,
    terms_draw_date: "29. desember 2026",
    show_on_offerwall: true,
    offerwall_priority: 76,
    status: "active",
  },
  {
    comp_key: "no_na_na_norskerabatter_189_189",
    cloak_slug: "na-norskerabatter-189",
    provider_network: "Nordic Affiliates",
    start_date_iso: "2026-10-10",
    end_date_iso: "2026-12-29",
    paid_lead_age_range: "18–75",
    sr_campaign_id: "189",
    sr_affiliate_id: "1020",
    geo: "no",
    category: "retail",
    title: "NorskeRabatter: Vinn et SuperGavekort på 10.000 kr. til valgfri butikk",
    subtitle: "Delta gratis i trekningen om SuperGavekort 10.000 kr. (189) (verdi 10.000 kr.). Tilbys av Spareklubb Norge / NorskeRabatter (3.50 EUR / 26.10 DKK CPL).",
    prize_name: "SuperGavekort 10.000 kr. (189)",
    prize_value_dkk: 10000.0,
    payout_dkk: 26.1,
    tracking_url_template: "https://publicistlank.rabattbrev.com/aff_c?offer_id=189&aff_id=1020&aff_sub=#s1#&aff_sub2=#s2#",
    exclusion_pools: ["pool_no_spareklubben", "pool_global_blacklist"],
    target_age_min: 18,
    target_age_max: 75,
    target_genders: [],
    target_interests: [{"id": "6003349442621", "name": "Gavekort, Rabatter & Konkurranser"}],
    excluded_zips: [],
    qualifying_questions: [],
    sponsors: SPONSORS_SPAREKLUBBEN,
    terms_draw_date: "29. desember 2026",
    show_on_offerwall: true,
    offerwall_priority: 77,
    status: "active",
  },
  {
    comp_key: "no_na_na_babyprodukter_10000_191",
    cloak_slug: "na-babyprodukter-10000",
    provider_network: "Nordic Affiliates",
    start_date_iso: "2026-10-15",
    end_date_iso: "2026-12-30",
    paid_lead_age_range: "18–75",
    sr_campaign_id: "191",
    sr_affiliate_id: "1020",
    geo: "no",
    category: "family",
    title: "Spareklubb Norge: Vinn 10.000 kr. til babyutstyr & barneprodukter",
    subtitle: "Delta gratis i trekningen om Babyutstyr Gavekort 10.000 kr. (verdi 10.000 kr.). Tilbys av Spareklubb Norge / NorskeRabatter (3.50 EUR / 26.10 DKK CPL).",
    prize_name: "Babyutstyr Gavekort 10.000 kr.",
    prize_value_dkk: 10000.0,
    payout_dkk: 26.1,
    tracking_url_template: "https://publicistlank.rabattbrev.com/aff_c?offer_id=191&aff_id=1020&aff_sub=#s1#&aff_sub2=#s2#",
    exclusion_pools: ["pool_no_spareklubben", "pool_global_blacklist"],
    target_age_min: 18,
    target_age_max: 75,
    target_genders: [],
    target_interests: [{"id": "6003349442621", "name": "Gavekort, Rabatter & Konkurranser"}],
    excluded_zips: [],
    qualifying_questions: [],
    sponsors: SPONSORS_SPAREKLUBBEN,
    terms_draw_date: "30. desember 2026",
    show_on_offerwall: true,
    offerwall_priority: 78,
    status: "active",
  },
  {
    comp_key: "no_na_na_amazon_10000_194",
    cloak_slug: "na-amazon-10000",
    provider_network: "Nordic Affiliates",
    start_date_iso: "2026-10-16",
    end_date_iso: "2026-12-30",
    paid_lead_age_range: "18–75",
    sr_campaign_id: "194",
    sr_affiliate_id: "1020",
    geo: "no",
    category: "retail",
    title: "Spareklubb Norge: Vinn et gavekort til Amazon på 10.000 kr.",
    subtitle: "Delta gratis i trekningen om Amazon Gavekort 10.000 kr. (verdi 10.000 kr.). Tilbys av Spareklubb Norge / NorskeRabatter (3.50 EUR / 26.10 DKK CPL).",
    prize_name: "Amazon Gavekort 10.000 kr.",
    prize_value_dkk: 10000.0,
    payout_dkk: 26.1,
    tracking_url_template: "https://publicistlank.rabattbrev.com/aff_c?offer_id=194&aff_id=1020&aff_sub=#s1#&aff_sub2=#s2#",
    exclusion_pools: ["pool_no_spareklubben", "pool_global_blacklist"],
    target_age_min: 18,
    target_age_max: 75,
    target_genders: [],
    target_interests: [{"id": "6003349442621", "name": "Gavekort, Rabatter & Konkurranser"}],
    excluded_zips: [],
    qualifying_questions: [],
    sponsors: SPONSORS_SPAREKLUBBEN,
    terms_draw_date: "30. desember 2026",
    show_on_offerwall: true,
    offerwall_priority: 79,
    status: "active",
  },
  {
    comp_key: "no_na_na_lancome_10000_201",
    cloak_slug: "na-lancome-10000",
    provider_network: "Nordic Affiliates",
    start_date_iso: "2026-10-18",
    end_date_iso: "2026-12-31",
    paid_lead_age_range: "18–75",
    sr_campaign_id: "201",
    sr_affiliate_id: "1020",
    geo: "no",
    category: "retail",
    title: "Spareklubb Norge: Vinn Lancôme skjønnhetsprodukter & gavekort på 10.000 kr.",
    subtitle: "Delta gratis i trekningen om Lancôme Gavekort 10.000 kr. (verdi 10.000 kr.). Tilbys av Spareklubb Norge / NorskeRabatter (3.50 EUR / 26.10 DKK CPL).",
    prize_name: "Lancôme Gavekort 10.000 kr.",
    prize_value_dkk: 10000.0,
    payout_dkk: 26.1,
    tracking_url_template: "https://publicistlank.rabattbrev.com/aff_c?offer_id=201&aff_id=1020&aff_sub=#s1#&aff_sub2=#s2#",
    exclusion_pools: ["pool_no_spareklubben", "pool_global_blacklist"],
    target_age_min: 18,
    target_age_max: 75,
    target_genders: [],
    target_interests: [{"id": "6003349442621", "name": "Gavekort, Rabatter & Konkurranser"}],
    excluded_zips: [],
    qualifying_questions: [],
    sponsors: SPONSORS_SPAREKLUBBEN,
    terms_draw_date: "31. desember 2026",
    show_on_offerwall: true,
    offerwall_priority: 80,
    status: "active",
  },
  {
    comp_key: "no_na_na_coop_10000_203",
    cloak_slug: "na-coop-10000",
    provider_network: "Nordic Affiliates",
    start_date_iso: "2026-10-19",
    end_date_iso: "2026-12-31",
    paid_lead_age_range: "18–75",
    sr_campaign_id: "203",
    sr_affiliate_id: "1020",
    geo: "no",
    category: "grocery",
    title: "Spareklubb Norge: Vinn et gavekort til COOP på 10.000 kr.",
    subtitle: "Delta gratis i trekningen om COOP Gavekort 10.000 kr. (verdi 10.000 kr.). Tilbys av Spareklubb Norge / NorskeRabatter (3.50 EUR / 26.10 DKK CPL).",
    prize_name: "COOP Gavekort 10.000 kr.",
    prize_value_dkk: 10000.0,
    payout_dkk: 26.1,
    tracking_url_template: "https://publicistlank.rabattbrev.com/aff_c?offer_id=203&aff_id=1020&aff_sub=#s1#&aff_sub2=#s2#",
    exclusion_pools: ["pool_no_spareklubben", "pool_global_blacklist"],
    target_age_min: 18,
    target_age_max: 75,
    target_genders: [],
    target_interests: [{"id": "6003349442621", "name": "Gavekort, Rabatter & Konkurranser"}],
    excluded_zips: [],
    qualifying_questions: [],
    sponsors: SPONSORS_SPAREKLUBBEN,
    terms_draw_date: "31. desember 2026",
    show_on_offerwall: true,
    offerwall_priority: 81,
    status: "active",
  },
  {
    comp_key: "no_na_na_dagligvarebutikk_10000_205",
    cloak_slug: "na-dagligvarebutikk-10000",
    provider_network: "Nordic Affiliates",
    start_date_iso: "2026-10-20",
    end_date_iso: "2026-12-31",
    paid_lead_age_range: "18–75",
    sr_campaign_id: "205",
    sr_affiliate_id: "1020",
    geo: "no",
    category: "grocery",
    title: "Spareklubb Norge: Vinn 10.000 kr. til din lokale dagligvarebutikk",
    subtitle: "Delta gratis i trekningen om Dagligvarebutikk Gavekort 10.000 kr. (verdi 10.000 kr.). Tilbys av Spareklubb Norge / NorskeRabatter (3.50 EUR / 26.10 DKK CPL).",
    prize_name: "Dagligvarebutikk Gavekort 10.000 kr.",
    prize_value_dkk: 10000.0,
    payout_dkk: 26.1,
    tracking_url_template: "https://publicistlank.rabattbrev.com/aff_c?offer_id=205&aff_id=1020&aff_sub=#s1#&aff_sub2=#s2#",
    exclusion_pools: ["pool_no_spareklubben", "pool_global_blacklist"],
    target_age_min: 18,
    target_age_max: 75,
    target_genders: [],
    target_interests: [{"id": "6003349442621", "name": "Gavekort, Rabatter & Konkurranser"}],
    excluded_zips: [],
    qualifying_questions: [],
    sponsors: SPONSORS_SPAREKLUBBEN,
    terms_draw_date: "31. desember 2026",
    show_on_offerwall: true,
    offerwall_priority: 82,
    status: "active",
  },
  {
    comp_key: "no_na_na_clas_ohlson_10000_210",
    cloak_slug: "na-clas-ohlson-10000",
    provider_network: "Nordic Affiliates",
    start_date_iso: "2026-10-21",
    end_date_iso: "2026-12-31",
    paid_lead_age_range: "18–75",
    sr_campaign_id: "210",
    sr_affiliate_id: "1020",
    geo: "no",
    category: "home",
    title: "Spareklubb Norge: Vinn et gavekort til Clas Ohlson på 10.000 kr.",
    subtitle: "Delta gratis i trekningen om Clas Ohlson Gavekort 10.000 kr. (verdi 10.000 kr.). Tilbys av Spareklubb Norge / NorskeRabatter (3.50 EUR / 26.10 DKK CPL).",
    prize_name: "Clas Ohlson Gavekort 10.000 kr.",
    prize_value_dkk: 10000.0,
    payout_dkk: 26.1,
    tracking_url_template: "https://publicistlank.rabattbrev.com/aff_c?offer_id=210&aff_id=1020&aff_sub=#s1#&aff_sub2=#s2#",
    exclusion_pools: ["pool_no_spareklubben", "pool_global_blacklist"],
    target_age_min: 18,
    target_age_max: 75,
    target_genders: [],
    target_interests: [{"id": "6003349442621", "name": "Gavekort, Rabatter & Konkurranser"}],
    excluded_zips: [],
    qualifying_questions: [],
    sponsors: SPONSORS_SPAREKLUBBEN,
    terms_draw_date: "31. desember 2026",
    show_on_offerwall: true,
    offerwall_priority: 83,
    status: "active",
  },
  {
    comp_key: "no_na_na_biltema_10000_211",
    cloak_slug: "na-biltema-10000",
    provider_network: "Nordic Affiliates",
    start_date_iso: "2026-10-22",
    end_date_iso: "2026-12-31",
    paid_lead_age_range: "18–75",
    sr_campaign_id: "211",
    sr_affiliate_id: "1020",
    geo: "no",
    category: "home",
    title: "Spareklubb Norge: Vinn et gavekort til Biltema på 10.000 kr.",
    subtitle: "Delta gratis i trekningen om Biltema Gavekort 10.000 kr. (verdi 10.000 kr.). Tilbys av Spareklubb Norge / NorskeRabatter (3.50 EUR / 26.10 DKK CPL).",
    prize_name: "Biltema Gavekort 10.000 kr.",
    prize_value_dkk: 10000.0,
    payout_dkk: 26.1,
    tracking_url_template: "https://publicistlank.rabattbrev.com/aff_c?offer_id=211&aff_id=1020&aff_sub=#s1#&aff_sub2=#s2#",
    exclusion_pools: ["pool_no_spareklubben", "pool_global_blacklist"],
    target_age_min: 18,
    target_age_max: 75,
    target_genders: [],
    target_interests: [{"id": "6003349442621", "name": "Gavekort, Rabatter & Konkurranser"}],
    excluded_zips: [],
    qualifying_questions: [],
    sponsors: SPONSORS_SPAREKLUBBEN,
    terms_draw_date: "31. desember 2026",
    show_on_offerwall: true,
    offerwall_priority: 84,
    status: "active",
  },
  {
    comp_key: "no_na_na_cubus_10000_212",
    cloak_slug: "na-cubus-10000",
    provider_network: "Nordic Affiliates",
    start_date_iso: "2026-10-23",
    end_date_iso: "2026-12-31",
    paid_lead_age_range: "18–75",
    sr_campaign_id: "212",
    sr_affiliate_id: "1020",
    geo: "no",
    category: "retail",
    title: "Spareklubb Norge: Vinn et gavekort til Cubus på 10.000 kr. (V2)",
    subtitle: "Delta gratis i trekningen om Cubus Gavekort 10.000 kr. (V2) (verdi 10.000 kr.). Tilbys av Spareklubb Norge / NorskeRabatter (3.50 EUR / 26.10 DKK CPL).",
    prize_name: "Cubus Gavekort 10.000 kr. (V2)",
    prize_value_dkk: 10000.0,
    payout_dkk: 26.1,
    tracking_url_template: "https://publicistlank.rabattbrev.com/aff_c?offer_id=212&aff_id=1020&aff_sub=#s1#&aff_sub2=#s2#",
    exclusion_pools: ["pool_no_spareklubben", "pool_global_blacklist"],
    target_age_min: 18,
    target_age_max: 75,
    target_genders: [],
    target_interests: [{"id": "6003349442621", "name": "Gavekort, Rabatter & Konkurranser"}],
    excluded_zips: [],
    qualifying_questions: [],
    sponsors: SPONSORS_SPAREKLUBBEN,
    terms_draw_date: "31. desember 2026",
    show_on_offerwall: true,
    offerwall_priority: 85,
    status: "active",
  },
  {
    comp_key: "no_na_na_europris_10000_214",
    cloak_slug: "na-europris-10000",
    provider_network: "Nordic Affiliates",
    start_date_iso: "2026-10-24",
    end_date_iso: "2026-12-31",
    paid_lead_age_range: "18–75",
    sr_campaign_id: "214",
    sr_affiliate_id: "1020",
    geo: "no",
    category: "grocery",
    title: "Spareklubb Norge: Vinn et gavekort til Europris på 10.000 kr.",
    subtitle: "Delta gratis i trekningen om Europris Gavekort 10.000 kr. (verdi 10.000 kr.). Tilbys av Spareklubb Norge / NorskeRabatter (3.50 EUR / 26.10 DKK CPL).",
    prize_name: "Europris Gavekort 10.000 kr.",
    prize_value_dkk: 10000.0,
    payout_dkk: 26.1,
    tracking_url_template: "https://publicistlank.rabattbrev.com/aff_c?offer_id=214&aff_id=1020&aff_sub=#s1#&aff_sub2=#s2#",
    exclusion_pools: ["pool_no_spareklubben", "pool_global_blacklist"],
    target_age_min: 18,
    target_age_max: 75,
    target_genders: [],
    target_interests: [{"id": "6003349442621", "name": "Gavekort, Rabatter & Konkurranser"}],
    excluded_zips: [],
    qualifying_questions: [],
    sponsors: SPONSORS_SPAREKLUBBEN,
    terms_draw_date: "31. desember 2026",
    show_on_offerwall: true,
    offerwall_priority: 86,
    status: "active",
  },
  {
    comp_key: "no_na_na_nille_10000_215",
    cloak_slug: "na-nille-10000",
    provider_network: "Nordic Affiliates",
    start_date_iso: "2026-10-25",
    end_date_iso: "2026-12-31",
    paid_lead_age_range: "18–75",
    sr_campaign_id: "215",
    sr_affiliate_id: "1020",
    geo: "no",
    category: "home",
    title: "Spareklubb Norge: Vinn et gavekort til Nille på 10.000 kr.",
    subtitle: "Delta gratis i trekningen om Nille Gavekort 10.000 kr. (verdi 10.000 kr.). Tilbys av Spareklubb Norge / NorskeRabatter (3.50 EUR / 26.10 DKK CPL).",
    prize_name: "Nille Gavekort 10.000 kr.",
    prize_value_dkk: 10000.0,
    payout_dkk: 26.1,
    tracking_url_template: "https://publicistlank.rabattbrev.com/aff_c?offer_id=215&aff_id=1020&aff_sub=#s1#&aff_sub2=#s2#",
    exclusion_pools: ["pool_no_spareklubben", "pool_global_blacklist"],
    target_age_min: 18,
    target_age_max: 75,
    target_genders: [],
    target_interests: [{"id": "6003349442621", "name": "Gavekort, Rabatter & Konkurranser"}],
    excluded_zips: [],
    qualifying_questions: [],
    sponsors: SPONSORS_SPAREKLUBBEN,
    terms_draw_date: "31. desember 2026",
    show_on_offerwall: true,
    offerwall_priority: 87,
    status: "active",
  },
  {
    comp_key: "no_na_na_normal_10000_216",
    cloak_slug: "na-normal-10000",
    provider_network: "Nordic Affiliates",
    start_date_iso: "2026-10-26",
    end_date_iso: "2026-12-31",
    paid_lead_age_range: "18–75",
    sr_campaign_id: "216",
    sr_affiliate_id: "1020",
    geo: "no",
    category: "retail",
    title: "Spareklubb Norge: Vinn et gavekort til NORMAL på 10.000 kr.",
    subtitle: "Delta gratis i trekningen om NORMAL Gavekort 10.000 kr. (verdi 10.000 kr.). Tilbys av Spareklubb Norge / NorskeRabatter (3.50 EUR / 26.10 DKK CPL).",
    prize_name: "NORMAL Gavekort 10.000 kr.",
    prize_value_dkk: 10000.0,
    payout_dkk: 26.1,
    tracking_url_template: "https://publicistlank.rabattbrev.com/aff_c?offer_id=216&aff_id=1020&aff_sub=#s1#&aff_sub2=#s2#",
    exclusion_pools: ["pool_no_spareklubben", "pool_global_blacklist"],
    target_age_min: 18,
    target_age_max: 75,
    target_genders: [],
    target_interests: [{"id": "6003349442621", "name": "Gavekort, Rabatter & Konkurranser"}],
    excluded_zips: [],
    qualifying_questions: [],
    sponsors: SPONSORS_SPAREKLUBBEN,
    terms_draw_date: "31. desember 2026",
    show_on_offerwall: true,
    offerwall_priority: 88,
    status: "active",
  },
  {
    comp_key: "no_na_na_pizzabakeren_10000_217",
    cloak_slug: "na-pizzabakeren-10000",
    provider_network: "Nordic Affiliates",
    start_date_iso: "2026-10-27",
    end_date_iso: "2026-12-31",
    paid_lead_age_range: "18–75",
    sr_campaign_id: "217",
    sr_affiliate_id: "1020",
    geo: "no",
    category: "grocery",
    title: "Spareklubb Norge: Vinn et gavekort til Pizzabakeren på 10.000 kr.",
    subtitle: "Delta gratis i trekningen om Pizzabakeren Gavekort 10.000 kr. (verdi 10.000 kr.). Tilbys av Spareklubb Norge / NorskeRabatter (3.50 EUR / 26.10 DKK CPL).",
    prize_name: "Pizzabakeren Gavekort 10.000 kr.",
    prize_value_dkk: 10000.0,
    payout_dkk: 26.1,
    tracking_url_template: "https://publicistlank.rabattbrev.com/aff_c?offer_id=217&aff_id=1020&aff_sub=#s1#&aff_sub2=#s2#",
    exclusion_pools: ["pool_no_spareklubben", "pool_global_blacklist"],
    target_age_min: 18,
    target_age_max: 75,
    target_genders: [],
    target_interests: [{"id": "6003349442621", "name": "Gavekort, Rabatter & Konkurranser"}],
    excluded_zips: [],
    qualifying_questions: [],
    sponsors: SPONSORS_SPAREKLUBBEN,
    terms_draw_date: "31. desember 2026",
    show_on_offerwall: true,
    offerwall_priority: 89,
    status: "active",
  },
];

const FALLBACK_POOLS: SRExclusionPool[] = [
  {
    pool_key: 'pool_no_onecall',
    name: 'NO — OneCall Standalone Pulje (productId=114927 / 114889 / 114874)',
    geo: 'no',
    description: 'Fælles 90-dages eksklusionspulje KUN på tværs af OneCall (Rema 1000, GoGift, VING). Påvirker IKKE Talkmore!',
    retention_days: 90,
    meta_custom_audience_id: '120234432380680250',
    meta_engagement_audience_id: '120234432380680250',
    auto_add_submitted_leads: true,
    auto_add_sr_rejections: true,
    total_hashes: 0,
    em_sha256_count: 0,
    ph_sha256_count: 0,
    md5_count: 0,
    rainbow_resolved_count: 0,
    sr_import_count: 0,
    internal_collected_count: 0,
    linked_competitions: ['no_onecall_rema1000_114927', 'no_onecall_gogift_114889', 'no_onecall_ving_114874'],
    last_synced_at: new Date().toISOString(),
  },
  {
    pool_key: 'pool_no_talkmore',
    name: 'NO — Talkmore Standalone Pulje (productId=114903 / groupId=1486)',
    geo: 'no',
    description: 'Fælles 45-dages eksklusionspulje KUN på tværs af Talkmore (GoGift, TUI, Iskremmaskin). Påvirker IKKE OneCall!',
    retention_days: 45,
    meta_custom_audience_id: 'ca_no_sr_talkmore',
    meta_engagement_audience_id: 'ea_no_talkmore_45d',
    auto_add_submitted_leads: true,
    auto_add_sr_rejections: true,
    total_hashes: 0,
    em_sha256_count: 0,
    ph_sha256_count: 0,
    md5_count: 0,
    rainbow_resolved_count: 0,
    sr_import_count: 0,
    internal_collected_count: 0,
    linked_competitions: ['no_talkmore_gogift_114903', 'no_talkmore_tui_114903', 'no_talkmore_iskremmaskin_114903'],
    last_synced_at: new Date().toISOString(),
  },
  {
    pool_key: 'pool_dk_eforbruger',
    name: 'DK — E-forbruger Leadshare (productId=114919 / groupId=1411)',
    geo: 'dk',
    description: '90-dages eksklusionspulje for E-forbruger Leadshare-kampagner (Apollo 32 DKK).',
    retention_days: 90,
    meta_custom_audience_id: 'ca_dk_sr_eforbruger',
    meta_engagement_audience_id: 'ea_dk_eforbruger_90d',
    auto_add_submitted_leads: true,
    auto_add_sr_rejections: true,
    total_hashes: 0,
    em_sha256_count: 0,
    ph_sha256_count: 0,
    md5_count: 0,
    rainbow_resolved_count: 0,
    sr_import_count: 0,
    internal_collected_count: 0,
    linked_competitions: ['dk_eforbruger_apollo_114919'],
    last_synced_at: new Date().toISOString(),
  },
  {
    pool_key: 'pool_dk_tryg',
    name: 'DK — Tryg Forsikring Standalone (productId=114888)',
    geo: 'dk',
    description: '90-dages eksklusionspulje KUN for Tryg (Silvan 10.000 kr., 22 DKK).',
    retention_days: 90,
    meta_custom_audience_id: 'ca_dk_sr_tryg',
    meta_engagement_audience_id: 'ea_dk_tryg_90d',
    auto_add_submitted_leads: true,
    auto_add_sr_rejections: true,
    total_hashes: 0,
    em_sha256_count: 0,
    ph_sha256_count: 0,
    md5_count: 0,
    rainbow_resolved_count: 0,
    sr_import_count: 0,
    internal_collected_count: 0,
    linked_competitions: ['dk_tryg_silvan_114888'],
    last_synced_at: new Date().toISOString(),
  },
  {
    pool_key: 'pool_dk_ase',
    name: 'DK — ASE A-kasse Standalone (productId=114875)',
    geo: 'dk',
    description: '120-dages eksklusionspulje KUN for ASE (Apollo 5.000 kr., 22 DKK).',
    retention_days: 120,
    meta_custom_audience_id: 'ca_dk_sr_ase',
    meta_engagement_audience_id: 'ea_dk_ase_120d',
    auto_add_submitted_leads: true,
    auto_add_sr_rejections: true,
    total_hashes: 0,
    em_sha256_count: 0,
    ph_sha256_count: 0,
    md5_count: 0,
    rainbow_resolved_count: 0,
    sr_import_count: 0,
    internal_collected_count: 0,
    linked_competitions: ['dk_ase_apollo_114875'],
    last_synced_at: new Date().toISOString(),
  },
  {
    pool_key: 'pool_no_spareklubben',
    name: 'NO — Nordic Affiliates / Spareklubb Norge & NorskeRabatter (aff_id=1020)',
    geo: 'no',
    description: 'Fælles 90-dages eksklusionspulje for alle 50 norske konkurrencer hos Nordic Affiliates (Spareklubb Norge & NorskeRabatter, 3,50 EUR / 26,10 DKK CPL). Tillader fuld kryds-monetisering efter OneCall og Talkmore!',
    retention_days: 90,
    meta_custom_audience_id: 'ca_no_na_spareklubben',
    meta_engagement_audience_id: 'ea_no_spareklubben_90d',
    auto_add_submitted_leads: true,
    auto_add_sr_rejections: true,
    total_hashes: 0,
    em_sha256_count: 0,
    ph_sha256_count: 0,
    md5_count: 0,
    rainbow_resolved_count: 0,
    sr_import_count: 0,
    internal_collected_count: 0,
    linked_competitions: ["no_na_na_supermarked_20_20", "no_na_na_mummi_royaldesign_39", "no_na_na_bensin_gavekort_40", "no_na_na_interior_royaldesign_50", "no_na_na_robotstovsuger_51", "no_na_na_delonghi_kaffequiz_53", "no_na_na_delonghi_direkte_54", "no_na_na_supermarked_ny_67", "no_na_na_bensin_direkte_73", "no_na_na_universalgavekort_76_76", "no_na_na_jula_gavekort_80", "no_na_na_hm_gavekort_81", "no_na_na_xxl_gavekort_82", "no_na_na_ikea_gavekort_83", "no_na_na_elsykkel_91_91", "no_na_na_norskerabatter_universal_111", "no_na_na_norskerabatter_elsykkel_prl_113", "no_na_na_norskerabatter_elsykkel_114", "no_na_na_norskerabatter_supermarked_115", "no_na_na_elkjop_gavekort_119", "no_na_na_vita_gavekort_120", "no_na_na_zara_gavekort_121", "no_na_na_freia_sjokolade_134", "no_na_na_cubus_gavekort_142_142", "no_na_na_carlings_gavekort_143", "no_na_na_sport1_gavekort_144", "no_na_na_apple_airpods_145", "no_na_na_garmin_polar_146", "no_na_na_fischer_ski_147", "no_na_na_omo_vaskepakke_148", "no_na_na_iphone_17_171", "no_na_na_kicks_clarins_174", "no_na_na_milka_10000_178", "no_na_na_rema_1000_180_180", "no_na_na_cocacola_5000_182", "no_na_na_burger_king_185", "no_na_na_supermarked_kampanje_188", "no_na_na_norskerabatter_189_189", "no_na_na_babyprodukter_10000_191", "no_na_na_amazon_10000_194", "no_na_na_lancome_10000_201", "no_na_na_coop_10000_203", "no_na_na_dagligvarebutikk_10000_205", "no_na_na_clas_ohlson_10000_210", "no_na_na_biltema_10000_211", "no_na_na_cubus_10000_212", "no_na_na_europris_10000_214", "no_na_na_nille_10000_215", "no_na_na_normal_10000_216", "no_na_na_pizzabakeren_10000_217"],
    last_synced_at: new Date().toISOString(),
  },
  {
    pool_key: 'pool_no_other_sr',
    name: 'NO — SmartResponse Øvrige Standalone (Allente / Northguard)',
    geo: 'no',
    description: 'Eksklusionspulje for øvrige norske SmartResponse standalone-kampagner (Allente TV, Northguard Drivhus).',
    retention_days: 90,
    meta_custom_audience_id: 'ca_no_sr_other',
    meta_engagement_audience_id: 'ea_no_other_90d',
    auto_add_submitted_leads: true,
    auto_add_sr_rejections: true,
    total_hashes: 0,
    em_sha256_count: 0,
    ph_sha256_count: 0,
    md5_count: 0,
    rainbow_resolved_count: 0,
    sr_import_count: 0,
    internal_collected_count: 0,
    linked_competitions: ["no_northguard_drivhus_114805", "no_allente_tvpakke_114764"],
    last_synced_at: new Date().toISOString(),
  },
  {
    pool_key: 'pool_global_blacklist',
    name: 'GLOBAL — Afima Robinson / Unsubscribe / Fraud Blacklist',
    geo: 'no',
    description: 'Global spærreliste på tværs af alle konkurrencer: Robinson/Reservasjonsregisteret, ugyldige numre og klagere.',
    retention_days: 3650,
    meta_custom_audience_id: 'ca_global_sr_blacklist',
    meta_engagement_audience_id: null,
    auto_add_submitted_leads: false,
    auto_add_sr_rejections: false,
    total_hashes: 0,
    em_sha256_count: 0,
    ph_sha256_count: 0,
    md5_count: 0,
    rainbow_resolved_count: 0,
    sr_import_count: 0,
    internal_collected_count: 0,
    linked_competitions: ['ALL'],
    last_synced_at: new Date().toISOString(),
  },
];

// Local state for fallback when Lambda backend is not reachable
const LOCAL_LEADS: SRLeadLedgerRow[] = [];
const LOCAL_PLANS: SRCampaignPlanRecord[] = [
  {
    plan_id: 'sr-no-onecall-rema1000-launch-v1',
    plan_key: 'sr-no-onecall-rema1000-launch-v1',
    comp_key: 'no_onecall_rema1000_114927',
    sr_campaign_id: '114927',
    geo: 'no',
    name: 'SR | NO | no_onecall_rema1000_114927 | 114927 | LeadAd',
    daily_budget_dkk: 300,
    status: 'built_paused',
    mode: 'live',
    ad_account_id: 'act_995004339585761',
    page_id: '1420182721178198',
    created_by: 'ai_preset',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    built_at: '2026-10-30T11:52:00+00:00',
    meta_objects: {
      ad_account_id: 'act_995004339585761',
      page_id: '1420182721178198',
      image_hash: 'f69d3c1b21d3e2fa7221e8d639fd950d',
      lead_form_id: '1548544003699895',
      campaign_id: '120253959843440349',
      adsets: [
        { segment_key: 'young', name: 'SR | NO | no_onecall_rema1000_114927 | 25–34 år | Alle køn | grocery_bargain', id: '120253959843670349' },
        { segment_key: 'core', name: 'SR | NO | no_onecall_rema1000_114927 | 35–49 år | Alle køn | grocery_bargain', id: '120253959844700349' },
        { segment_key: 'senior', name: 'SR | NO | no_onecall_rema1000_114927 | 50–65 år | Alle køn | grocery_bargain', id: '120253959845320349' },
      ],
      ads: [
        {
          segment_key: 'young',
          name: 'SR Ad | no_onecall_rema1000_114927 | 18–34 år (Grøn knap)',
          id: '120254009057040349',
          ad_id: '120254009057040349',
          creative_id: '1111854251459193',
          image_hash: '8f1915663986c35fd349efe7885c4f08',
          preview_url: 'https://fb.me/1Wvyy834m90sljP',
          target_age_label: '18–34 år',
          local_image_url: '/smartresponse-assets/rema1000_age_25_34_1080x1080.png',
        },
        {
          segment_key: 'core',
          name: 'SR Ad | no_onecall_rema1000_114927 | 35–49 år (Grøn knap)',
          id: '120254009058150349',
          ad_id: '120254009058150349',
          creative_id: '1093645883274036',
          image_hash: 'f69d3c1b21d3e2fa7221e8d639fd950d',
          preview_url: 'https://fb.me/2c1ccdwo2jVtXUH',
          target_age_label: '35–49 år',
          local_image_url: '/smartresponse-assets/rema1000_age_35_49_1080x1080.png',
        },
        {
          segment_key: 'senior',
          name: 'SR Ad | no_onecall_rema1000_114927 | 50+ år (Grøn knap)',
          id: '120254009059800349',
          ad_id: '120254009059800349',
          creative_id: '1472211295109529',
          image_hash: 'bc2f9303555b22274f59fee9432718bf',
          preview_url: 'https://fb.me/26VAhzKzEwEhiwr',
          target_age_label: '50+ år',
          local_image_url: '/smartresponse-assets/rema1000_age_50_65_1080x1080.png',
        },
      ],
    },
    build_calls: [],
    last_error: null,
    options: {
      comp_key: 'no_onecall_rema1000_114927',
      daily_budget_dkk: 300,
      split_by_age: true,
      split_by_gender: false,
      strict_targeting: true,
      age_min: 25,
      age_max: 65,
      interest_pack: 'grocery_bargain',
      creative_template: 'age_buttons',
    },
    plan: {
      comp_key: 'no_onecall_rema1000_114927',
      sr_campaign_id: '114927',
      geo: 'no',
      country: 'NO',
      campaign: {
        name: 'SR | NO | no_onecall_rema1000_114927 | 114927 | LeadAd',
        objective: 'OUTCOME_LEADS',
        special_ad_categories: [],
        buying_type: 'AUCTION',
        status: 'PAUSED',
      },
      ad_sets: [
        {
          segment_key: 'young',
          name: 'SR | NO | no_onecall_rema1000_114927 | 25–34 år | Alle køn | grocery_bargain',
          daily_budget_dkk: 90,
          optimization_goal: 'LEAD_GENERATION',
          destination_type: 'ON_AD',
          status: 'PAUSED',
          targeting: {
            geo_locations: { countries: ['NO'] },
            age_min: 25,
            age_max: 34,
            excluded_custom_audiences: [
              { id: '120234432380680250', name: 'NO — OneCall 90d Pulje' },
              { id: 'ca_global_sr_blacklist', name: 'Global Blacklist' },
            ],
            targeting_automation: { advantage_audience: 0 },
          },
        },
        {
          segment_key: 'core',
          name: 'SR | NO | no_onecall_rema1000_114927 | 35–49 år | Alle køn | grocery_bargain',
          daily_budget_dkk: 135,
          optimization_goal: 'LEAD_GENERATION',
          destination_type: 'ON_AD',
          status: 'PAUSED',
          targeting: {
            geo_locations: { countries: ['NO'] },
            age_min: 35,
            age_max: 49,
            excluded_custom_audiences: [
              { id: '120234432380680250', name: 'NO — OneCall 90d Pulje' },
              { id: 'ca_global_sr_blacklist', name: 'Global Blacklist' },
            ],
            targeting_automation: { advantage_audience: 0 },
          },
        },
        {
          segment_key: 'senior',
          name: 'SR | NO | no_onecall_rema1000_114927 | 50–65 år | Alle køn | grocery_bargain',
          daily_budget_dkk: 75,
          optimization_goal: 'LEAD_GENERATION',
          destination_type: 'ON_AD',
          status: 'PAUSED',
          targeting: {
            geo_locations: { countries: ['NO'] },
            age_min: 50,
            age_max: 65,
            excluded_custom_audiences: [
              { id: '120234432380680250', name: 'NO — OneCall 90d Pulje' },
              { id: 'ca_global_sr_blacklist', name: 'Global Blacklist' },
            ],
            targeting_automation: { advantage_audience: 0 },
          },
        },
      ],
      instant_form: {
        name: 'SR Form | no_onecall_rema1000_114927',
        locale: 'nb_NO',
        privacy_policy: {
          url: 'https://premieportal.com/partnere/no_onecall_rema1000_114927/',
          link_text: 'Konkurransebetingelser & Personvern',
        },
        thank_you_page: {
          title: 'Tusen takk for din deltakelse! 🎁',
          button_text: 'Se flere gratis konkurranser →',
          website_url: 'https://premieportal.com/konkurrencer/?done=no_onecall_rema1000_114927&geo=no&s1={{ad.id}}&s2={{campaign.id}}',
        },
      },
      compliance: {
        passed: true,
        blocker_count: 0,
        checks: [
          { code: 'NO_FINANCE_RESTRICTION', severity: 'blocker', passed: true, message: 'special_ad_categories er [] (NONE) — fuld alders-, køns- og interessesegmentering aktiv.' },
          { code: 'ONECALL_FEEDORDER_SPEC', severity: 'blocker', passed: true, message: 'OneCall (114927) udelader e-mail/postnr/adresse i Instant Form og sender {8_cifret_tlf}@gmail.com i felt 148 til FeedOrder.aspx.' },
          { code: 'EXCLUSION_POOLS_ATTACHED', severity: 'blocker', passed: true, message: 'Alle Ad Sets ekskluderer pool_no_onecall (90d) og pool_global_blacklist.' },
        ],
      },
    },
  },
];

async function srRequest<T>(path: string, init?: RequestInit, fallback?: () => T): Promise<T> {
  try {
    const res = await fetch(`${API_BASE_URL}/api/smartresponse${path}`, init);
    if (res.ok) {
      return (await res.json()) as T;
    }
  } catch {
    // Fallback to clean local state (with 0 fake spend) if backend route is not yet deployed remotely
  }
  if (fallback) {
    return fallback();
  }
  throw new Error(`SmartResponse API request failed: ${path}`);
}

export async function fetchSRDashboard(geo: SRGeo = 'all', days: number = 14): Promise<SRDashboardOverview> {
  const qs = new URLSearchParams({ geo, days: String(days) }).toString();
  return srRequest<SRDashboardOverview>(`/dashboard?${qs}`, undefined, () => {
    const filteredComps = FALLBACK_COMPETITIONS.filter((c) => geo === 'all' || c.geo === geo);
    const byComp: SRCompetitionPerfRow[] = filteredComps.map((c) => {
      const compLeads = LOCAL_LEADS.filter((l) => l.comp_key === c.comp_key);
      const raw = compLeads.length;
      const preBlk = compLeads.filter((l) => l.dedup_status.startsWith('blocked')).length;
      const srDup = compLeads.filter((l) => l.dedup_status === 'rejected_sr_api_dup').length;
      const acc = compLeads.filter((l) => ['accepted', 'dry_run_accepted', 'live_accepted'].includes(l.sr_api_status)).length;
      const primRev = Number((acc * c.payout_dkk).toFixed(2));
      return {
        comp_key: c.comp_key,
        sr_campaign_id: c.sr_campaign_id,
        geo: c.geo,
        category: c.category,
        title: c.title,
        prize_name: c.prize_name,
        payout_dkk: c.payout_dkk,
        exclusion_pools: c.exclusion_pools,
        spend_dkk: 0,
        raw_leads: raw,
        pre_dedup_blocked: preBlk,
        sr_api_duplicates: srDup,
        sr_api_accepted: acc,
        duplicate_rate_pct: raw ? Number((((preBlk + srDup) / raw) * 100).toFixed(1)) : 0,
        gross_cpl_dkk: 0,
        net_accepted_cpl_dkk: 0,
        primary_revenue_dkk: primRev,
        offerwall_clicks: 0,
        offerwall_conversions: 0,
        offerwall_revenue_dkk: 0,
        total_revenue_dkk: primRev,
        net_profit_dkk: primRev,
        roas: 0,
        status: c.status,
      };
    });

    const totRaw = byComp.reduce((s, r) => s + r.raw_leads, 0);
    const totPre = byComp.reduce((s, r) => s + r.pre_dedup_blocked, 0);
    const totSrDup = byComp.reduce((s, r) => s + r.sr_api_duplicates, 0);
    const totAcc = byComp.reduce((s, r) => s + r.sr_api_accepted, 0);
    const totPrimRev = byComp.reduce((s, r) => s + r.primary_revenue_dkk, 0);

    return {
      geo,
      days,
      safety_modes: {
        sr_api_mode: 'dry_run',
        meta_capi_mode: 'dry_run',
        meta_builder_mode: 'dry_run',
      },
      meta_insights_status: {
        account_id: 'act_995004339585761',
        account_name: 'Afima - PremiePortalen (SmartResponse)',
        last_synced_at: null,
        sync_mode: 'ready_for_first_campaign',
      },
      summary: {
        spend_dkk: 0,
        raw_leads: totRaw,
        pre_dedup_blocked: totPre,
        sr_api_duplicates: totSrDup,
        sr_api_accepted: totAcc,
        duplicate_rate_pct: totRaw ? Number((((totPre + totSrDup) / totRaw) * 100).toFixed(1)) : 0,
        gross_cpl_dkk: 0,
        net_accepted_cpl_dkk: 0,
        primary_revenue_dkk: totPrimRev,
        offerwall_clicks: 0,
        offerwall_conversions: 0,
        offerwall_revenue_dkk: 0,
        total_revenue_dkk: totPrimRev,
        net_profit_dkk: totPrimRev,
        roas: 0,
      },
      by_competition: byComp,
      by_age_segment: [
        {
          age_bucket: '18–24 år (Ekskluderes på TM-kampagner <25 år)',
          spend_dkk: 0,
          raw_leads: 0,
          accepted_leads: 0,
          duplicate_rate_pct: 0,
          gross_cpl_dkk: 0,
          net_accepted_cpl_dkk: 0,
          offerwall_epc_dkk: 0,
          roas: 0,
          recommendation: 'Hold ekskluderet (TM-annoncører kræver 25–70 år)',
        },
        {
          age_bucket: '25–34 år',
          spend_dkk: 0,
          raw_leads: 0,
          accepted_leads: 0,
          duplicate_rate_pct: 0,
          gross_cpl_dkk: 0,
          net_accepted_cpl_dkk: 0,
          offerwall_epc_dkk: 0,
          roas: 0,
          recommendation: 'Stærk på OneCall (32 NOK), Talkmore (40 NOK) & ASE',
        },
        {
          age_bucket: '35–49 år (Kernesegment)',
          spend_dkk: 0,
          raw_leads: 0,
          accepted_leads: 0,
          duplicate_rate_pct: 0,
          gross_cpl_dkk: 0,
          net_accepted_cpl_dkk: 0,
          offerwall_epc_dkk: 0,
          roas: 0,
          recommendation: 'Højeste accept hos OneCall, Talkmore, E-forbruger & Tryg',
        },
        {
          age_bucket: '50–65 år',
          spend_dkk: 0,
          raw_leads: 0,
          accepted_leads: 0,
          duplicate_rate_pct: 0,
          gross_cpl_dkk: 0,
          net_accepted_cpl_dkk: 0,
          offerwall_epc_dkk: 0,
          roas: 0,
          recommendation: 'Lavest dublet-rate og høj konvertering på rejse- og dagligvarepræmier',
        },
      ],
      by_gender: [
        {
          gender: 'Kvinder (118=241)',
          raw_leads: 0,
          accepted_leads: 0,
          spend_dkk: 0,
          share_pct: 0,
          gross_cpl_dkk: 0,
          net_accepted_cpl_dkk: 0,
          duplicate_rate_pct: 0,
          offerwall_ctr_pct: 0,
          roas: 0,
        },
        {
          gender: 'Mænd (118=240)',
          raw_leads: 0,
          accepted_leads: 0,
          spend_dkk: 0,
          share_pct: 0,
          gross_cpl_dkk: 0,
          net_accepted_cpl_dkk: 0,
          duplicate_rate_pct: 0,
          offerwall_ctr_pct: 0,
          roas: 0,
        },
      ],
      ad_winners: [],
      guardian: {
        config: {
          mode: 'alert_only',
          max_spend_zero_accepted_dkk: 100,
          max_duplicate_rate_pct: 25,
          max_cpl_multiplier: 1.25,
          min_leads_for_dup_alert: 5,
          updated_at: new Date().toISOString(),
        },
        alerts_count: 0,
        alerts: [],
        health_checks: [
          {
            key: 'cloudflare_portal_no',
            label: 'Norsk Konkurrenceportal (https://premieportal.com)',
            status: 'ok',
            detail: 'LIVE på Cloudflare Worker (afima-competition-portal) med /partnere/ og /konkurrencer/ offerwall',
          },
          {
            key: 'meta_assets_no',
            label: 'Meta Ad Account & Facebook Page (Norge)',
            status: 'ok',
            detail: 'Ad Account act_995004339585761 + Page PremiePortalen (1420182721178198) i Business 209169658044315',
          },
          {
            key: 'sr_orderfeed_api',
            label: 'SmartResponse FeedOrder.aspx Integration',
            status: 'ok',
            detail: 'Endpoint: https://webservice.smartresponse-media.com/FeedOrder.aspx (sourceId=100168, partnerId=1336, 15-min sync)',
          },
          {
            key: 'meta_leadgen_webhook',
            label: 'Meta Lead Ads Real-Time Webhook',
            status: 'ok',
            detail: 'POST /api/smartresponse/webhooks/meta-leadgen (validerer 7753/8886/alder + dummy-mail {tlf}@gmail.com)',
          },
        ],
        total_leads_tracked: LOCAL_LEADS.length,
      },
      campaign_plans: LOCAL_PLANS,
      recent_leads: LOCAL_LEADS,
      brands: FALLBACK_BRANDS,
    };
  });
}

export async function syncSRMetaInsights(days: number = 14) {
  return srRequest(
    '/meta/sync-insights',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ days }),
    },
    () => ({
      last_synced_at: new Date().toISOString(),
      sync_mode: 'local_ready',
      account_id: 'act_995004339585761',
      ads: [],
      demographics: [],
    })
  );
}

export async function updateSRGuardianConfig(payload: {
  mode?: 'alert_only' | 'auto_pause';
  max_spend_zero_accepted_dkk?: number;
  max_duplicate_rate_pct?: number;
  max_cpl_multiplier?: number;
}) {
  return srRequest(
    '/guardian/config',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
    () => ({ success: true, config: payload })
  );
}

export async function fetchSRMetaPlans(geo: SRGeo = 'all'): Promise<{ success: boolean; plans: SRCampaignPlanRecord[] }> {
  const res = await srRequest(`/meta/plans?geo=${geo}`, undefined, () => ({
    success: true,
    plans: LOCAL_PLANS.filter((p) => geo === 'all' || p.geo === geo),
  }));
  return normalizeRemaCompetitionAndPlan(res);
}

export async function buildSRPlanInMeta(plan_id: string, mode: 'dry_run' | 'live' = 'dry_run') {
  return srRequest(
    '/meta/build',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ plan_id, mode }),
    },
    () => {
      const found = LOCAL_PLANS.find((p) => p.plan_id === plan_id) || LOCAL_PLANS[0];
      const adsets = (found.plan?.ad_sets || []).map((a: any, i: number) => ({
        segment_key: a.segment_key,
        name: a.name,
        id: `adset_${a.segment_key}_${100 + i}`,
      }));
      const ads = (found.plan?.ad_sets || []).map((a: any, i: number) => ({
        segment_key: a.segment_key,
        name: `SR Ad | ${found.comp_key} | ${a.segment_key}`,
        id: `ad_${a.segment_key}_${200 + i}`,
        creative_id: `cre_${a.segment_key}_${300 + i}`,
      }));
      found.status = mode === 'live' ? 'built_paused' : 'dry_run_built';
      found.built_at = new Date().toISOString();
      found.meta_objects = {
        ad_account_id: 'act_995004339585761',
        page_id: '1420182721178198',
        image_hash: 'hash_onecall_rema1000_1080x1080',
        lead_form_id: `form_${found.sr_campaign_id}_live`,
        campaign_id: `camp_${found.sr_campaign_id}_paused`,
        adsets,
        ads,
      };
      found.build_calls = [
        { step: 1, object_type: 'adimage', method: 'POST', path: 'act_995004339585761/adimages', summary: 'Upload 1080×1080 PNG kreativ til Meta Ad Account' },
        { step: 2, object_type: 'leadgen_form', method: 'POST', path: '1420182721178198/leadgen_forms', summary: 'Opret Meta Instant Form på Page 1420182721178198 (uden e-mail/postnr for OneCall)' },
        { step: 3, object_type: 'campaign', method: 'POST', path: 'act_995004339585761/campaigns', summary: `Opret Kampagne '${found.name}' (PAUSED, special_ad_categories=[])` },
        ...adsets.map((s: any, idx: number) => ({
          step: 4 + idx,
          object_type: 'adset',
          method: 'POST',
          path: 'act_995004339585761/adsets',
          summary: `Opret Segmenteret Ad Set '${s.name}' + AdCreative + Ad (PAUSED)`,
        })),
      ];
      return {
        success: true,
        mode,
        plan_record: found,
        meta_objects: found.meta_objects,
        calls: found.build_calls,
      };
    }
  );
}

export async function setSRPlanMetaStatus(plan_id: string, action: 'activate' | 'pause') {
  return srRequest(
    '/meta/status',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ plan_id, action }),
    },
    () => {
      const found = LOCAL_PLANS.find((p) => p.plan_id === plan_id) || LOCAL_PLANS[0];
      found.status = action === 'activate' ? 'active' : 'paused';
      found.updated_at = new Date().toISOString();
      return {
        success: true,
        plan_id,
        new_status: found.status,
        meta_status: action === 'activate' ? 'ACTIVE' : 'PAUSED',
        plan_record: found,
      };
    }
  );
}

export async function fetchSRAiContext() {
  return srRequest('/ai-context', undefined, () => ({
    module: 'Afima SmartResponse AI Lead Gen, Segmentation & Competition Engine',
    url: 'https://afima.ai/smartresponse',
    live_infrastructure: {
      meta_business_portfolio_id: '209169658044315',
      meta_ad_account_id: 'act_995004339585761',
      meta_ad_account_name: 'Afima - PremiePortalen (SmartResponse)',
      meta_page_no_id: '1420182721178198',
      meta_page_no_name: 'PremiePortalen',
      portal_no_domain: 'https://premieportal.com',
      portal_dk_domain: 'https://praemieportalen.dk',
    },
    smartresponse_feedorder_spec: {
      endpoint: 'https://webservice.smartresponse-media.com/FeedOrder.aspx',
      documentation_url: 'https://webservice.smartresponse-media.com/FeedOrderDocumentation.aspx?sourceId=100168&partnerId=1336&productId=114927',
      sourceId: '100168',
      partnerId: '1336',
      sync_cadence: 'Synchronous format check on POST; advertiser approval & duplicate status updated every 15 minutes 24/7 in Orderfeed.',
    },
  }));
}

export async function fetchSRExclusions(geo: SRGeo = 'all'): Promise<{
  pools: SRExclusionPool[];
  recent_imports: SRSuppressionImportAudit[];
  recent_leads?: SRLeadLedgerRow[];
}> {
  return srRequest(`/exclusions?geo=${geo}`, undefined, () => ({
    pools: FALLBACK_POOLS.filter((p) => geo === 'all' || p.geo === geo || p.pool_key === 'pool_global_blacklist'),
    recent_imports: [],
    recent_leads: LOCAL_LEADS,
  }));
}

export async function importSRSuppressionList(payload: {
  pool_key: string;
  content: string;
  input_format: string;
  filename: string;
  geo?: string;
}) {
  const lines = payload.content.split(/\r?\n/).filter((l) => l.trim().length > 0);
  return srRequest(
    '/exclusions/import',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
    () => ({
      success: true,
      import_audit: {
        id: Math.floor(Math.random() * 9000) + 10,
        pool_key: payload.pool_key,
        filename: payload.filename,
        input_format: payload.input_format === 'auto' ? 'raw_csv' : payload.input_format,
        total_rows: lines.length,
        valid_hashes: lines.length * 2,
        inserted_new: lines.length,
        rainbow_resolved: 0,
        meta_sync_status: 'ready_to_sync',
        created_at: new Date().toISOString(),
      },
    })
  );
}

export async function syncSRPoolToMeta(pool_key: string, dry_run: boolean = true) {
  return srRequest(
    '/exclusions/sync-meta',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pool_key, dry_run }),
    },
    () => {
      const pool = FALLBACK_POOLS.find((p) => p.pool_key === pool_key) || FALLBACK_POOLS[0];
      return {
        success: true,
        mode: dry_run ? 'dry_run' : 'live',
        pool_key,
        meta_custom_audience_id: pool.meta_custom_audience_id,
        email_sha256_synced: pool.em_sha256_count,
        phone_sha256_synced: pool.ph_sha256_count,
        total_synced: pool.em_sha256_count + pool.ph_sha256_count,
        synced_at: new Date().toISOString(),
      };
    }
  );
}

export async function simulateSRLeadIntake(payload: {
  comp_key: string;
  email?: string;
  phone: string;
  first_name?: string;
  last_name?: string;
  age: number;
  gender: string;
  zip_code?: string;
  qualifying_answers?: Record<string, string>;
  consent_given: boolean;
  persist_lead?: boolean;
}) {
  return srRequest(
    '/leads/intake',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
    () => {
      const comp = FALLBACK_COMPETITIONS.find((c) => c.comp_key === payload.comp_key) || FALLBACK_COMPETITIONS[0];
      const cleanPhone = payload.phone.replace(/\D/g, '').slice(-8);
      const isOneCall = ['114927', '114889', '114874'].includes(comp.sr_campaign_id);
      const effectiveEmail = isOneCall ? `${cleanPhone}@gmail.com` : payload.email || `${cleanPhone}@gmail.com`;
      const opAnswer = (payload.qualifying_answers?.current_operator || 'Telenor').toLowerCase();
      const firmAnswer = (payload.qualifying_answers?.company_phone || 'Nei').toLowerCase();

      const opDisqualified =
        (isOneCall && (opAnswer.includes('onecall') || opAnswer.includes('telia') || opAnswer.includes('mycall'))) ||
        (comp.sr_campaign_id === '114903' && (opAnswer.includes('talkmore') || opAnswer.includes('telenor') || opAnswer.includes('bedrift')));
      const firmDisqualified = isOneCall && firmAnswer.includes('ja');
      const isAgeOut = payload.age < comp.target_age_min || payload.age > comp.target_age_max;
      const passed = !opDisqualified && !firmDisqualified && !isAgeOut && payload.consent_given;

      const birthDate = `${new Date().getFullYear() - payload.age}-05-15`;
      const opCode = opAnswer.includes('talkmore')
        ? '6428'
        : opAnswer.includes('ice')
        ? '6430'
        : opAnswer.includes('telia')
        ? '342'
        : opAnswer.includes('onecall')
        ? '492'
        : '812';

      const orderfeedPayload: Record<string, string> = {
        sourceId: '100168',
        partnerId: '1336',
        productId: comp.sr_campaign_id,
        '522': payload.first_name || 'Kari',
        '523': payload.last_name || 'Nordmann',
        '148': effectiveEmail,
        '117': cleanPhone,
        '118': payload.gender === 'm' ? '240' : '241',
        '119': birthDate,
        '628': comp.geo === 'no' ? '1430' : '629',
        '7753': opCode,
        '8886': firmAnswer.includes('ja') ? '80' : '81',
        '3631': comp.title,
        '5752': `https://premieportal.com/partnere/${comp.comp_key}/`,
      };

      return {
        success: true,
        verdict: {
          passed,
          status: passed ? 'passed' : 'blocked_product_condition',
          matched_pool_key: null,
          reason: passed
            ? `Godkendt mod FeedOrderDocumentation.aspx (productId=${comp.sr_campaign_id}, 148=${effectiveEmail})`
            : opDisqualified
            ? `Afvist af Product Condition 7753 (Operatør "${payload.qualifying_answers?.current_operator}" udelukket for ${comp.prize_name})`
            : firmDisqualified
            ? 'Afvist af Product Condition 8886!=80 (Firmatelefon=Ja afvises hos OneCall)'
            : `Alder (${payload.age} år) uden for kvalificeringskrav (${comp.target_age_min}–${comp.target_age_max} år)`,
        },
        orderfeed: {
          accepted: passed,
          status: passed ? 'dry_run_accepted' : 'rejected_condition',
          orderfeed_payload: orderfeedPayload,
        },
        capi_gate: {
          positive_event_fired: passed,
          event_name: passed ? 'Lead' : null,
          value_dkk: passed ? comp.payout_dkk : 0,
          reason: passed
            ? 'Positive CAPI Lead event sendt med DKK payout (Lead godkendt af dedup + SR FeedOrder)'
            : 'CAPI Lead event BLOKERET — Meta algoritmen fodres IKKE med dubletter/afviste leads',
        },
        thank_you_offerwall_url: `https://premieportal.com/konkurrencer/?done=${comp.comp_key}&geo=${comp.geo}&age=${payload.age}`,
      };
    }
  );
}

function normalizeRemaCompetitionAndPlan<T>( rawData: T ): T {
  const data: any = rawData;
  if (!data) return rawData;
  const latestRema = FALLBACK_COMPETITIONS[0];
  const latestPlan = LOCAL_PLANS[0];
  if (Array.isArray(data.competitions)) {
    data.competitions = data.competitions.map((c: any) =>
      c?.comp_key === 'no_onecall_rema1000_114927'
        ? {
            ...c,
            title: latestRema.title,
            subtitle: latestRema.subtitle,
            prize_name: latestRema.prize_name,
            prize_value_dkk: latestRema.prize_value_dkk,
            terms_draw_date: latestRema.terms_draw_date,
            qualifying_questions: latestRema.qualifying_questions,
          }
        : c
    );
  }
  if (Array.isArray(data.campaign_plans)) {
    data.campaign_plans = data.campaign_plans.map((p: any) =>
      p?.plan_id === 'sr-no-onecall-rema1000-launch-v1' || p?.comp_key === 'no_onecall_rema1000_114927'
        ? {
            ...p,
            built_at: latestPlan.built_at,
            meta_objects: latestPlan.meta_objects,
          }
        : p
    );
  }
  if (Array.isArray(data.plans)) {
    data.plans = data.plans.map((p: any) =>
      p?.plan_id === 'sr-no-onecall-rema1000-launch-v1' || p?.comp_key === 'no_onecall_rema1000_114927'
        ? {
            ...p,
            built_at: latestPlan.built_at,
            meta_objects: latestPlan.meta_objects,
          }
        : p
    );
  }
  return data as T;
}

export async function fetchSRCompetitionsAndBrands(geo: SRGeo = 'all') {
  const res = await srRequest(
    `/competitions?geo=${geo}`,
    undefined,
    () => ({
      competitions: FALLBACK_COMPETITIONS.filter((c) => geo === 'all' || c.geo === geo),
      brands: FALLBACK_BRANDS,
      exclusion_pools: FALLBACK_POOLS,
      campaign_plans: LOCAL_PLANS,
      interest_packs: [
        { key: 'broad_none', name: 'Broad (Kun Alder/Køn + Eksklusionspuljer)', description: 'Lader kreativet og alders-/køns-splittet segmentere billigst muligt.' },
        { key: 'grocery_bargain', name: 'Dagligvarer & Gavekort (REMA 1000 / GoGift)', description: 'Supermarkeder, tilbudsaviser og familiebudget.' },
        { key: 'travel_charter', name: 'Rejser, Solferie & Charter (VING / TUI / Apollo)', description: 'Charterrejser, flybilletter, hoteller og familieferie.' },
        { key: 'homeowners_diy', name: 'Boligejere, Hus & Have (Tryg Silvan 10.000 kr.)', description: 'Husejere, havearbejde og gør-det-selv.' },
        { key: 'electronics_gaming', name: 'Køkken, Elektronik & Hjem (Iskremmaskin)', description: 'Køkkenudstyr, forbrugerelektronik og smart home.' },
        { key: 'family_parents', name: 'Forældre & Børnefamilier', description: 'Forældre med børn 0–12 år & familieoplevelser.' },
      ],
      creative_templates: ['age_buttons', 'quiz_question', 'gift_boxes'],
    })
  );
  return normalizeRemaCompetitionAndPlan(res);
}

export async function generateSRCampaignPlan(payload: Record<string, any>) {
  return srRequest(
    '/meta/plans',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
    () => {
      const comp = FALLBACK_COMPETITIONS.find((c) => c.comp_key === payload.comp_key) || FALLBACK_COMPETITIONS[0];
      const brand = FALLBACK_BRANDS.find((b) => b.geo === comp.geo) || FALLBACK_BRANDS[0];
      const dailyBudget = Number(payload.daily_budget_dkk || 300);
      const splitByAge = payload.split_by_age !== false;
      const splitByGender = Boolean(payload.split_by_gender);
      const strictTargeting = payload.strict_targeting !== false;
      const brackets = splitByAge
        ? [
            { key: 'young', label: `${comp.target_age_min}–34 år`, age_min: comp.target_age_min, age_max: 34, share: 0.3 },
            { key: 'core', label: '35–49 år', age_min: 35, age_max: 49, share: 0.45 },
            { key: 'senior', label: `50–${comp.target_age_max} år`, age_min: 50, age_max: comp.target_age_max, share: 0.25 },
          ]
        : [{ key: 'all_ages', label: `${comp.target_age_min}–${comp.target_age_max} år`, age_min: comp.target_age_min, age_max: comp.target_age_max, share: 1.0 }];

      const excludedAudiences = comp.exclusion_pools.map((pk) => ({
        id: `ca_${pk}`,
        name: pk,
      }));

      let adSets = brackets.map((b) => ({
        segment_key: b.key,
        name: `SR | ${comp.geo.toUpperCase()} | ${comp.comp_key} | ${b.label} | Alle køn | ${payload.interest_pack || 'broad'}`,
        daily_budget_dkk: Math.round(dailyBudget * b.share),
        optimization_goal: 'LEAD_GENERATION',
        destination_type: 'ON_AD',
        status: 'PAUSED',
        targeting: {
          geo_locations: { countries: [comp.geo.toUpperCase()] },
          age_min: b.age_min,
          age_max: b.age_max,
          genders: payload.genders || [],
          excluded_custom_audiences: excludedAudiences,
          targeting_automation: { advantage_audience: strictTargeting ? 0 : 1 },
        },
      }));

      if (splitByGender && (!payload.genders || payload.genders.length === 0)) {
        const expanded: any[] = [];
        for (const s of adSets) {
          expanded.push({
            ...s,
            segment_key: `${s.segment_key}_f`,
            name: s.name.replace('Alle køn', 'Kvinder'),
            daily_budget_dkk: Math.round(s.daily_budget_dkk / 2),
            targeting: { ...s.targeting, genders: [2] },
          });
          expanded.push({
            ...s,
            segment_key: `${s.segment_key}_m`,
            name: s.name.replace('Alle køn', 'Mænd'),
            daily_budget_dkk: Math.round(s.daily_budget_dkk / 2),
            targeting: { ...s.targeting, genders: [1] },
          });
        }
        adSets = expanded;
      }

      const planObj = {
        comp_key: comp.comp_key,
        sr_campaign_id: comp.sr_campaign_id,
        geo: comp.geo,
        country: comp.geo.toUpperCase(),
        campaign: {
          name: `SR | ${comp.geo.toUpperCase()} | ${comp.comp_key} | ${comp.sr_campaign_id} | LeadAd`,
          objective: 'OUTCOME_LEADS',
          special_ad_categories: [],
          buying_type: 'AUCTION',
          status: 'PAUSED',
        },
        ad_sets: adSets,
        instant_form: {
          name: `SR Form | ${comp.comp_key}`,
          locale: comp.geo === 'no' ? 'nb_NO' : 'da_DK',
          privacy_policy: {
            url: `https://${brand.domain}/partnere/${comp.comp_key}/`,
            link_text: 'Konkurrencebetingelser, Privatlivspolitik & Sponsorliste',
          },
          thank_you_page: {
            title: 'Tusen takk for din deltakelse! 🎁',
            button_text: 'Se flere gratis konkurranser →',
            website_url: `https://${brand.domain}/konkurrencer/?done=${comp.comp_key}&geo=${comp.geo}&s1={{ad.id}}&s2={{campaign.id}}`,
          },
        },
        ad_creative: {
          headline: `🎁 ${comp.title}`,
          description: `Værdi ${comp.prize_value_dkk} kr. • Gratis deltagelse (${comp.target_age_min}+)`,
          template: payload.creative_template || 'age_buttons',
        },
        compliance: {
          passed: true,
          blocker_count: 0,
          checks: [
            { code: 'NO_FINANCE_RESTRICTION', severity: 'blocker', passed: true, message: 'special_ad_categories er [] (NONE) — fuld alders-, køns-, postnummer- og interessesegmentering er aktiv.' },
            { code: 'EXCLUSION_POOLS_ATTACHED', severity: 'blocker', passed: true, message: `Alle Ad Sets ekskluderer tilknyttede SmartResponse & Afima eksklusionspuljer (${comp.exclusion_pools.join(', ')}).` },
            { code: 'FEEDORDER_SPEC_VERIFIED', severity: 'blocker', passed: true, message: `FeedOrder.aspx (sourceId=100168, partnerId=1336, productId=${comp.sr_campaign_id}) valideret.` },
          ],
        },
      };

      const planKey = payload.plan_key || `sr-${comp.geo}-${comp.sr_campaign_id}-${Date.now().toString().slice(-4)}`;
      const rec: SRCampaignPlanRecord = {
        plan_id: planKey,
        plan_key: planKey,
        comp_key: comp.comp_key,
        sr_campaign_id: comp.sr_campaign_id,
        geo: comp.geo,
        name: planObj.campaign.name,
        daily_budget_dkk: dailyBudget,
        status: payload.action === 'queue' || payload.action === 'stage_campaign' ? 'queued' : 'validated',
        mode: 'ready',
        ad_account_id: brand.meta_ad_account_id,
        page_id: brand.meta_page_id,
        created_by: 'operator',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        plan: planObj,
      };
      if (payload.action === 'queue' || payload.action === 'stage_campaign') {
        LOCAL_PLANS.unshift(rec);
      }
      return {
        success: true,
        plan: planObj,
        plan_record: rec,
        all_plans: LOCAL_PLANS,
      };
    }
  );
}

export async function saveSRPortalBrand(payload: Partial<SRPortalBrand> & { geo: 'dk' | 'no' }) {
  return srRequest(
    '/portal/brand',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
    () => {
      const idx = FALLBACK_BRANDS.findIndex((b) => b.geo === payload.geo);
      if (idx >= 0) {
        FALLBACK_BRANDS[idx] = { ...FALLBACK_BRANDS[idx], ...payload };
      }
      return { success: true, brand: FALLBACK_BRANDS[idx] };
    }
  );
}

export async function saveSRCompetition(payload: Partial<SRCompetition> & { comp_key: string }) {
  return srRequest(
    '/competitions',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
    () => {
      const idx = FALLBACK_COMPETITIONS.findIndex((c) => c.comp_key === payload.comp_key);
      if (idx >= 0) {
        FALLBACK_COMPETITIONS[idx] = { ...FALLBACK_COMPETITIONS[idx], ...payload } as SRCompetition;
        return { success: true, competition: FALLBACK_COMPETITIONS[idx] };
      }
      const created = { ...FALLBACK_COMPETITIONS[0], ...payload } as SRCompetition;
      FALLBACK_COMPETITIONS.unshift(created);
      return { success: true, competition: created };
    }
  );
}


export async function scrapeCompetitionFromAffiliateLink(payload: {
  affiliate_url: string;
  provider_network?: string;
  geo?: 'dk' | 'no';
  payout_dkk?: number;
  auto_publish?: boolean;
}) {
  return srRequest(
    '/competitions/scrape-link',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
    () => {
      const rawUrl = payload.affiliate_url.trim();
      let pid = 'ny';
      try {
        const u = new URL(rawUrl);
        pid = u.searchParams.get('productId') || u.searchParams.get('id') || 'ny';
      } catch {}
      const isNo = rawUrl.includes('.no') || rawUrl.includes('norge') || payload.geo === 'no';
      const geo: 'dk' | 'no' = isNo ? 'no' : 'dk';
      const net =
        rawUrl.includes('svarnorge') || rawUrl.includes('svardanmark') || rawUrl.includes('e-forbruger')
          ? 'SmartResponse'
          : rawUrl.includes('adtraction')
          ? 'Adtraction'
          : rawUrl.includes('adservice')
          ? 'Adservice'
          : payload.provider_network || 'SmartResponse';
      const slug = `premie-${pid}`;
      const domain = geo === 'no' ? 'premieportal.com' : 'praemieportalen.dk';
      const scraped = {
        comp_key: `${geo}_${slug.replace(/-/g, '_')}`,
        cloak_slug: slug,
        cloak_url: `https://${domain}/go/${slug}`,
        provider_network: net,
        sr_campaign_id: pid,
        sr_affiliate_id: '1336',
        geo,
        category: 'retail',
        title: isNo ? `Vinn gavekort (Produkt ${pid})` : `Vind gavekort (Produkt ${pid})`,
        subtitle: isNo
          ? 'Gratis og uforpliktende deltakelse (18+ år).'
          : 'Gratis og uforpligtende deltagelse (18+ år).',
        prize_name: `Gavekort (${pid})`,
        prize_value_dkk: 5000,
        payout_dkk: payload.payout_dkk || 25,
        start_date_iso: new Date().toISOString().slice(0, 10),
        end_date_iso: '2026-12-22',
        terms_draw_date: isNo ? '22. desember 2026' : '22. december 2026',
        target_age_min: 18,
        paid_lead_age_range: '25–70',
        tracking_url_template: rawUrl,
        status: 'active' as const,
      };
      if (payload.auto_publish) {
        FALLBACK_COMPETITIONS.unshift({
          ...FALLBACK_COMPETITIONS[0],
          ...scraped,
        });
      }
      return {
        success: true,
        published: Boolean(payload.auto_publish),
        scraped,
      };
    }
  );
}

export async function runPremiePortalenScheduler(reference_date?: string) {
  return srRequest(
    '/scheduler/run',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reference_date }),
    },
    () => ({
      success: true,
      reference_date: reference_date || new Date().toISOString().slice(0, 10),
      paused_meta_plans: [],
      affiliate_links_synced: FALLBACK_COMPETITIONS.length,
      by_geo: {
        no: { alle_count: 61, nye_count: 18, slutter_snart_count: 4, tidligere_premier_count: 2 },
        dk: { alle_count: 3, nye_count: 3, slutter_snart_count: 2, tidligere_premier_count: 0 },
      },
      competitions: FALLBACK_COMPETITIONS,
    })
  );
}


export interface SRPortalLeadCompetition {
  comp_key: string;
  title: string;
  category?: string;
  clicked_at?: string;
  clicks?: number;
}

export interface SRPortalLead {
  lead_id: string;
  name: string;
  email: string;
  geo: 'no' | 'dk';
  consent_accepted: boolean;
  source_comp_key: string;
  source_comp_title: string;
  last_comp_key?: string;
  last_comp_title?: string;
  interested_competitions: SRPortalLeadCompetition[];
  total_clicks: number;
  ip_country?: string;
  created_at: string;
  last_active_at: string;
}

export async function fetchSRPortalLeads(params?: {
  geo?: string;
  search?: string;
}): Promise<{ success: boolean; count: number; total_stored: number; leads: SRPortalLead[] }> {
  const qs = new URLSearchParams();
  if (params?.geo && params.geo !== 'all') qs.set('geo', params.geo);
  if (params?.search) qs.set('search', params.search);
  const queryStr = qs.toString() ? `?${qs.toString()}` : '';

  const byEmail = new Map<string, SRPortalLead>();

  // 1. Fetch directly from PremiePortalen Edge KV database (real-time, 0ms lag)
  try {
    const edgeResp = await fetch(`https://premieportal.com/api/portal-leads${queryStr}`);
    if (edgeResp.ok) {
      const edgeData = await edgeResp.json();
      if (Array.isArray(edgeData.leads)) {
        for (const item of edgeData.leads) {
          if (item && item.email) {
            byEmail.set(String(item.email).toLowerCase(), item);
          }
        }
      }
    }
  } catch {}

  // 2. Also merge with AWS Lambda RDS PostgreSQL table (`admin.sr_portal_leads`)
  try {
    const rdsResp = await fetch(`${API_BASE_URL}/api/smartresponse/portal-leads${queryStr}`);
    if (rdsResp.ok) {
      const rdsData = await rdsResp.json();
      if (Array.isArray(rdsData.leads)) {
        for (const item of rdsData.leads) {
          if (item && item.email) {
            const k = String(item.email).toLowerCase();
            if (!byEmail.has(k)) {
              byEmail.set(k, item);
            }
          }
        }
      }
    }
  } catch {}

  const merged = Array.from(byEmail.values()).sort((a, b) =>
    String(b.last_active_at || b.created_at || '').localeCompare(String(a.last_active_at || a.created_at || ''))
  );

  return {
    success: true,
    count: merged.length,
    total_stored: merged.length,
    leads: merged,
  };
}


export interface SRPortalDayStats {
  pageviews: number;
  uniques: number;
  clicks: number;
  clicks_by_comp?: Record<string, number>;
  by_source?: Record<string, { visits: number; clicks: number }>;
}

export interface SRPortalStats {
  success: boolean;
  human_only: boolean;
  total_pageviews: number;
  unique_visitors: number;
  total_clicks: number;
  clicks_by_comp: Record<string, number>;
  by_source: Record<string, { visits: number; clicks: number }>;
  today: {
    day_key: string;
    pageviews: number;
    unique_visitors: number;
    clicks: number;
    clicks_by_comp: Record<string, number>;
    by_source: Record<string, { visits: number; clicks: number }>;
  };
  by_day: Record<string, SRPortalDayStats>;
  updated_at: string | null;
}

export async function fetchSRPortalStats(): Promise<SRPortalStats> {
  const todayIso = new Date().toISOString().slice(0, 10);
  try {
    const resp = await fetch('https://premieportal.com/api/portal-stats');
    if (resp.ok) {
      const data = await resp.json();
      const byDay = data.by_day || {};
      const todayRaw = data.today || byDay[todayIso] || {};
      return {
        success: true,
        human_only: true,
        total_pageviews: Number(data.total_pageviews || 0),
        unique_visitors: Number(data.unique_visitors || 0),
        total_clicks: Number(data.total_clicks || 0),
        clicks_by_comp: data.clicks_by_comp || {},
        by_source: data.by_source || {},
        today: {
          day_key: todayRaw.day_key || todayIso,
          pageviews: Number(todayRaw.pageviews || 0),
          unique_visitors: Number(todayRaw.unique_visitors ?? todayRaw.uniques ?? 0),
          clicks: Number(todayRaw.clicks || 0),
          clicks_by_comp: todayRaw.clicks_by_comp || {},
          by_source: todayRaw.by_source || {},
        },
        by_day: byDay,
        updated_at: data.updated_at || null,
      };
    }
  } catch {}
  return {
    success: false,
    human_only: true,
    total_pageviews: 0,
    unique_visitors: 0,
    total_clicks: 0,
    clicks_by_comp: {},
    by_source: {},
    today: {
      day_key: todayIso,
      pageviews: 0,
      unique_visitors: 0,
      clicks: 0,
      clicks_by_comp: {},
      by_source: {},
    },
    by_day: {},
    updated_at: null,
  };
}
